import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { getCategoryProfile } from "../category-profiles";
import { applyCategoryPreset, buildProfileAdjustedIntakeSteps, createDefaultIntakeData, getEffectiveIntakeSchema, isUpsellContractType, resolveConsultationProductName } from "../form-schema";
import { getPartIndexForStepId } from "../form-schema/parts";
import { DEFAULT_CATEGORY_TAXONOMY_CONFIG, createCategoryOptions, getAllTaxonomyCategoryIds, getCategoryDisplayBreadcrumb, getCategoryPath, getTaxonomyNode, getVisibleCategoryGroups, normalizeCategoryTaxonomyConfig, sortTaxonomyNodes, slugify, validateCategoryTaxonomy, type CategoryTaxonomyConfig, type CategoryTaxonomyNode, type CategoryTaxonomyNodeStatus } from "../category-taxonomy";
import { CONSULTATION_PRODUCT_TARGET_ID, createFormCatalogFromSteps, DEFAULT_FORM_CONFIG, getDefaultContractTypeForContext, mergeFormCatalog, reconcileCatalogWithRuntime, sanitizeRuleOptionIds, targetIdForField, targetIdForFieldOption, targetIdForRepeater, targetIdForRepeaterField, targetIdForRepeaterFieldOption, targetIdForStep, targetIdForUpload, type FormCatalogItem, type FormCatalogKind, type FormConfigDiagnostic, type FormConfigVersion, type FormRule, type FormRuleCondition, type FormRuleEffect } from "../form-config";
import type { FieldOption, FileUploadDefinition, IntakeCategoryId, IntakeData, IntakeDomain, IntakeField, IntakeStep, RepeaterField } from "../types";
import { AdminApiError, createAdminApiToken, createPartnerAccount, createPartnership, deleteAdminInvite, deleteAdminSubmission, duplicateAdminInvite, duplicateAdminSubmission, fetchAdminApiTokens, fetchAdminCategoryTaxonomy, fetchAdminFormConfig, fetchAdminInvites, fetchAdminMe, fetchAdminSubmission, fetchAdminSubmissions, loginAdmin, loginAdminWithTwoFactor, logoutAdmin, publishAdminCategoryTaxonomy, publishAdminFormConfig, requestSubmissionRevision, revealAdminPassword, revokeAdminApiToken, updateSubmissionSpot, type AdminApiToken, type AdminInviteSummary, type AdminSubmissionDetail, type AdminSubmissionFile, type AdminSubmissionSummary, type AdminUser, type DuplicatedDraftInvite, type PartnerAccountInput, type RequestSubmissionRevisionInput } from "./admin-api";
import {
  buildStepMatrixTargets,
  categoryLabel,
  cleanEffect,
  createAllScenarioCatalog,
  createFormConfigPreview,
  createScenarioData,
  createScopeCondition,
  DEFAULT_PREVIEW_TOGGLES,
  filterCatalogTargets,
  findEditorRule,
  flattenEffectiveTargets,
  createConsultationSeedEffect,
  FORM_CONFIG_DRAFT_STORAGE_KEY,
  FORM_CONFIG_SELECTED_GROUP_STORAGE_KEY,
  formatTargetStatus,
  editorRuleId,
  getConsultationSeedMode,
  groupStepTargetsByPart,
  isTextEntryTarget,
  loadFormConfigDraft,
  loadStoredString,
  matrixRowClass,
  matrixTargetLabel,
  nextQuickEffect,
  nextStepId,
  normalizeEditorConfig,
  orderStepTargets,
  quickPillClass,
  scopeLabel,
  UserSurveyStepPreview,
  type ConsultationSeedMode,
  type EditorScopeType,
  type PreviewToggles,
  type TargetStatus,
} from "./form-config-editor-model";
import {
  CategoryTaxonomyDiagnosticsList,
  createCustomProfileId,
  formatCategoryTaxonomyBlockingMessage,
  hasUnsavedTaxonomyChanges,
  inferDomainForGroup,
  splitLines,
  statusLabel,
  type SavedTaxonomyState,
} from "./category-taxonomy-editor-model";
import { getAdminSubmissionIdFromPathname, getAdminSubmissionPath, getAdminViewFromPathname, getAdminViewPath, pushAdminPath, useAdminSearchParam, type AdminView } from "./admin-navigation";
import { SubmissionDocumentPanel } from "./SubmissionDocumentPanel";
import { AdminSubmissionReviewSheet } from "./AdminSubmissionReviewSheet";

const PAGE_SIZE = 20;
const CATEGORY_OPTIONS = createCategoryOptions(DEFAULT_CATEGORY_TAXONOMY_CONFIG);

type AuthState =
  | { status: "checking"; user: null }
  | { status: "anonymous"; user: null }
  | { status: "authenticated"; user: AdminUser };

type ListState = {
  loading: boolean;
  error: string | null;
  items: AdminSubmissionSummary[];
  page: number;
  total: number;
  totalPages: number;
};

type DetailState = {
  loading: boolean;
  error: string | null;
  item: AdminSubmissionDetail | null;
};

type InviteListState = {
  loading: boolean;
  error: string | null;
  items: AdminInviteSummary[];
  page: number;
  total: number;
  totalPages: number;
};

export type CategoryRequestTaxonomyState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; config: CategoryTaxonomyConfig };

type AdminToastState = {
  kind: "success" | "error";
  message: string;
};

type SidebarTokenState = {
  loading: boolean;
  expanded: boolean;
  rawToken: string | null;
  tokens: AdminApiToken[];
};

const CATEGORY_TAXONOMY_DRAFT_STORAGE_KEY = "meet-the-world:admin-category-taxonomy-draft:v1";

export default function AdminApp() {
  const [auth, setAuth] = useState<AuthState>({ status: "checking", user: null });
  const [adminView, setAdminView] = useState<AdminView>(() => getAdminViewFromPathname(window.location.pathname));
  const [query, setQuery] = useAdminSearchParam("q", "", adminView === "submissions");
  const [categoryId, setCategoryId] = useAdminSearchParam("category", "", adminView === "submissions");
  const [queryDraft, setQueryDraft] = useState(query);
  const [selectedId, setSelectedId] = useState(() => getAdminSubmissionIdFromPathname(window.location.pathname));
  const [list, setList] = useState<ListState>({ loading: false, error: null, items: [], page: 1, total: 0, totalPages: 1 });
  const [inviteList, setInviteList] = useState<InviteListState>({ loading: false, error: null, items: [], page: 1, total: 0, totalPages: 1 });
  const [detail, setDetail] = useState<DetailState>({ loading: false, error: null, item: null });
  const [revisionCreating, setRevisionCreating] = useState(false);
  const [submissionDeleting, setSubmissionDeleting] = useState(false);
  const [submissionDuplicating, setSubmissionDuplicating] = useState(false);
  const [duplicatingInviteId, setDuplicatingInviteId] = useState<string | null>(null);
  const [deletingInviteId, setDeletingInviteId] = useState<string | null>(null);
  const [toast, setToast] = useState<AdminToastState | null>(null);
  const [tokenState, setTokenState] = useState<SidebarTokenState>({ loading: false, expanded: false, rawToken: null, tokens: [] });
  const toastTimerRef = useRef<number | null>(null);

  useEffect(() => {
    let alive = true;
    fetchAdminMe()
      .then((user) => { if (alive) setAuth({ status: "authenticated", user }); })
      .catch(() => { if (alive) setAuth({ status: "anonymous", user: null }); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      setSelectedId(getAdminSubmissionIdFromPathname(window.location.pathname));
      setAdminView(getAdminViewFromPathname(window.location.pathname));
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => setQueryDraft(query), [query]);

  useEffect(() => {
    if (auth.status !== "authenticated" || adminView !== "submissions") return;
    void loadList(1);
  }, [auth.status, adminView, query, categoryId]);

  useEffect(() => {
    if (auth.status !== "authenticated" || adminView !== "invites") return;
    void loadInvites(1);
  }, [auth.status, adminView]);

  useEffect(() => () => {
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
  }, []);

  useEffect(() => {
    if (auth.status !== "authenticated" || !selectedId) {
      setDetail({ loading: false, error: null, item: null });
      return;
    }
    let alive = true;
    setDetail((current) => ({ ...current, loading: true, error: null }));
    fetchAdminSubmission(selectedId)
      .then((item) => { if (alive) setDetail({ loading: false, error: null, item }); })
      .catch((error) => {
        if (!alive) return;
        const message = errorMessage(error);
        setDetail({ loading: false, error: null, item: null });
        showToast(message);
      });
    return () => { alive = false; };
  }, [auth.status, selectedId]);

  useEffect(() => {
    if (!selectedId && list.items.length > 0) setSelectedSubmission(list.items[0].id, false);
  }, [selectedId, list.items]);

  async function loadList(page: number) {
    setList((current) => ({ ...current, loading: true, error: null, page }));
    try {
      const response = await fetchAdminSubmissions({ page, pageSize: PAGE_SIZE, q: query, categoryId });
      setList({ loading: false, error: null, items: response.items, page: response.pagination.page, total: response.pagination.total, totalPages: response.pagination.totalPages });
    } catch (error) {
      setList((current) => ({ ...current, loading: false, error: null }));
      showToast(errorMessage(error));
    }
  }

  async function loadInvites(page: number) {
    setInviteList((current) => ({ ...current, loading: true, error: null, page }));
    try {
      const response = await fetchAdminInvites({ page, pageSize: PAGE_SIZE });
      setInviteList({ loading: false, error: null, items: response.items, page: response.pagination.page, total: response.pagination.total, totalPages: response.pagination.totalPages });
    } catch (error) {
      setInviteList((current) => ({ ...current, loading: false, error: null }));
      showToast(errorMessage(error));
    }
  }

  function submitSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setQuery(queryDraft);
  }

  function setSelectedSubmission(id: string, push = true) {
    setSelectedId(id);
    if (push) pushAdminPath(getAdminSubmissionPath(id));
  }

  function selectAdminView(view: AdminView) {
    setAdminView(view);
    pushAdminPath(view === "submissions" && selectedId ? getAdminSubmissionPath(selectedId) : getAdminViewPath(view));
  }

  async function handleLogout() {
    await logoutAdmin().catch(() => undefined);
    setAuth({ status: "anonymous", user: null });
    setSelectedId(null);
    setAdminView("submissions");
    setQuery("");
    setQueryDraft("");
    setCategoryId("");
    window.history.pushState(null, "", "/admin");
  }

  function showToast(message: string, kind: AdminToastState["kind"] = "error", durationMs = 10000) {
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    setToast({ kind, message });
    if (durationMs > 0) {
      toastTimerRef.current = window.setTimeout(() => setToast(null), durationMs);
    }
  }

  async function copyToolboxToken() {
    setTokenState((current) => ({ ...current, loading: true, expanded: true }));
    try {
      const created = await createAdminApiToken({ name: `toolbox ${new Date().toISOString().slice(0, 10)}`, expiresInDays: 365 });
      const tokenText = `Authorization: Bearer ${created.rawToken}`;
      setTokenState((current) => ({ ...current, rawToken: tokenText, tokens: [created.token, ...current.tokens.filter((token) => token.id !== created.token.id)] }));
      try {
        if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(tokenText);
        else copyTextWithFallback(tokenText);
        showToast("Toolbox API 토큰을 복사했습니다.", "success");
      } catch {
        showToast("클립보드 복사에 실패했습니다. 왼쪽 하단 토큰 값을 직접 복사해주세요.", "error", 0);
      }
      void refreshSidebarTokens();
    } catch (error) {
      showToast(errorMessage(error), "error", 0);
    } finally {
      setTokenState((current) => ({ ...current, loading: false }));
    }
  }

  async function refreshSidebarTokens() {
    if (auth.status !== "authenticated" || auth.user.role !== "SUPER_ADMIN") return;
    try {
      const tokens = await fetchAdminApiTokens();
      setTokenState((current) => ({ ...current, tokens }));
    } catch (error) {
      showToast(errorMessage(error), "error", 0);
    }
  }

  async function revokeSidebarToken(token: AdminApiToken) {
    if (token.revokedAt) return;
    try {
      await revokeAdminApiToken(token.id);
      setTokenState((current) => ({ ...current, tokens: current.tokens.map((item) => item.id === token.id ? { ...item, revokedAt: new Date().toISOString() } : item) }));
      showToast("API 토큰을 폐기했습니다.", "success");
    } catch (error) {
      showToast(errorMessage(error), "error", 0);
    }
  }

  async function copyRawSidebarToken() {
    if (!tokenState.rawToken) return;
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(tokenState.rawToken);
      else copyTextWithFallback(tokenState.rawToken);
      showToast("Toolbox API 토큰을 복사했습니다.", "success");
    } catch {
      showToast("브라우저가 복사를 차단했습니다. 토큰을 직접 선택해 복사해주세요.", "error", 0);
    }
  }

  async function copySubmissionLink(submissionId: string) {
    const url = new URL(getAdminSubmissionPath(submissionId), window.location.origin).toString();
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(url);
      else copyTextWithFallback(url);
      showToast("제출정보 링크를 복사했습니다.", "success");
    } catch {
      showToast(`복사하지 못했습니다. 직접 복사해주세요: ${url}`, "error", 0);
    }
  }

  async function copyInviteLink(invite: AdminInviteSummary) {
    const url = new URL(invite.path, window.location.origin).toString();
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(url);
      else copyTextWithFallback(url);
      showToast("초대 링크를 복사했습니다.", "success");
    } catch {
      showToast(url, "error", 0);
    }
  }

  async function handleCreatePartnerAccount(input: PartnerAccountInput) {
    if (!detail.item) return;
    try {
      await createPartnerAccount(detail.item.id, input);
      showToast(`${detail.item.adminLoginId ?? "파트너"} 계정을 생성했습니다.`, "success");
    } catch (error) {
      showToast(errorMessage(error), "error", 0);
      throw error;
    }
  }

  async function handleUpdateSpot(spotId: number | null) {
    if (!detail.item) return;
    const submissionId = detail.item.id;
    try {
      const savedSpotId = await updateSubmissionSpot(submissionId, spotId);
      setDetail((current) => current.item?.id === submissionId ? { ...current, item: { ...current.item, spotId: savedSpotId } } : current);
      setList((current) => ({
        ...current,
        items: current.items.map((item) => item.id === submissionId ? { ...item, spotId: savedSpotId } : item),
      }));
      showToast(savedSpotId ? `스팟 ${savedSpotId}번을 연결했습니다.` : "스팟 번호를 지웠습니다.", "success");
    } catch (error) {
      showToast(errorMessage(error), "error", 0);
      throw error;
    }
  }

  async function handleCreatePartnership() {
    if (!detail.item) return;
    try {
      const partnership = await createPartnership(detail.item.id);
      setDetail((current) => current.item ? {
        ...current,
        item: { ...current.item, partnershipCode: partnership.code, partnershipCreationStatus: "CREATED" },
      } : current);
      setList((current) => ({
        ...current,
        items: current.items.map((item) => item.id === detail.item?.id
          ? { ...item, partnershipCode: partnership.code, partnershipCreationStatus: "CREATED" }
          : item),
      }));
      showToast(`Product 거래처 ${partnership.code}번을 생성했습니다.`, "success");
    } catch (error) {
      showToast(errorMessage(error), "error", 0);
      throw error;
    }
  }

  // 첨부가 많으면 복제 응답까지 수 초가 걸려 클립보드 권한이 만료될 수 있다. 성공·실패 모두 URL을 노출해 손으로 복사할 수 있게 한다.
  async function announceDuplicatedInvite(label: string, duplicated: DuplicatedDraftInvite) {
    const url = new URL(duplicated.invite.path, window.location.origin).toString();
    const summary = `${label} 복제본 초대 링크를 만들었습니다. 첨부 ${duplicated.copiedFileCount}개 포함.`;
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(url);
      else copyTextWithFallback(url);
      showToast(`${summary} 링크를 복사했습니다: ${url}`, "success", 0);
    } catch {
      showToast(`${summary} 링크를 복사하지 못했습니다. 직접 복사해주세요: ${url}`, "error", 0);
    }
  }

  async function handleDuplicateSubmission() {
    const target = detail.item;
    if (!target) return;
    setSubmissionDuplicating(true);
    try {
      await announceDuplicatedInvite(target.storeNameKo || target.id, await duplicateAdminSubmission(target.id));
    } catch (error) {
      showToast(errorMessage(error), "error", 0);
    } finally {
      setSubmissionDuplicating(false);
    }
  }

  async function handleDuplicateInvite(invite: AdminInviteSummary) {
    setDuplicatingInviteId(invite.id);
    try {
      await announceDuplicatedInvite(invite.storeNameKo || invite.draftId, await duplicateAdminInvite(invite.id));
      await loadInvites(inviteList.page);
    } catch (error) {
      showToast(errorMessage(error), "error", 0);
    } finally {
      setDuplicatingInviteId(null);
    }
  }

  async function handleDeleteInvite(invite: AdminInviteSummary) {
    setDeletingInviteId(invite.id);
    try {
      await deleteAdminInvite(invite.id);
      showToast(`${invite.storeNameKo || invite.draftId} 초대 링크를 삭제했습니다.`, "success");
      await loadInvites(inviteList.items.length === 1 && inviteList.page > 1 ? inviteList.page - 1 : inviteList.page);
    } catch (error) {
      showToast(errorMessage(error), "error", 0);
      throw error;
    } finally {
      setDeletingInviteId(null);
    }
  }

  async function handleDeleteSubmission() {
    const target = detail.item;
    if (!target) return;
    setSubmissionDeleting(true);
    try {
      await deleteAdminSubmission(target.id);
      const remainingOnPage = list.items.filter((item) => item.id !== target.id);
      setList((current) => ({ ...current, items: remainingOnPage, total: Math.max(0, current.total - 1) }));
      setDetail({ loading: false, error: null, item: null });
      setSelectedId(null);
      pushAdminPath("/admin");
      showToast(`${target.storeNameKo || target.id} 제출 건을 삭제했습니다.`, "success");
      await loadList(remainingOnPage.length === 0 && list.page > 1 ? list.page - 1 : list.page);
    } catch (error) {
      showToast(errorMessage(error), "error", 0);
      throw error;
    } finally {
      setSubmissionDeleting(false);
    }
  }

  async function handleRequestRevision(input: RequestSubmissionRevisionInput) {
    if (!detail.item) return;
    setRevisionCreating(true);
    try {
      const invite = await requestSubmissionRevision(detail.item.id, input);
      const url = new URL(invite.path, window.location.origin).toString();
      try {
        if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(url);
        else copyTextWithFallback(url);
        showToast(input.rotateLink ? "보충 요청 링크를 새로 발급하고 복사했습니다. 기존 링크는 사용할 수 없습니다." : "기존 파트너 링크를 보충 요청용으로 다시 열고 복사했습니다.", "success", 0);
      } catch {
        showToast(input.rotateLink ? `새 보충 요청 링크를 만들었습니다. 직접 복사해주세요: ${url}` : `기존 파트너 링크를 다시 열었습니다. 직접 복사해주세요: ${url}`, "error", 0);
      }
    } catch (error) {
      showToast(errorMessage(error), "error", 0);
      throw error;
    } finally {
      setRevisionCreating(false);
    }
  }

  if (auth.status === "checking") return <AdminLoading />;
  if (auth.status === "anonymous") return <AdminLogin onLogin={(user) => setAuth({ status: "authenticated", user })} />;

  return (
    <main className="admin-shell">
      <header className="admin-topbar">
        <div className="admin-brand-block">
          <div>
            <h1>크리에이트립 상품요청서</h1>
            <p>제출 현황과 신청서 설정을 확인합니다.</p>
          </div>
        </div>
        <div className="admin-user-block">
          <span>{auth.user.name || auth.user.email}</span>
          <button className="button ghost compact" type="button" onClick={handleLogout}>로그아웃</button>
        </div>
      </header>

      <section className="admin-layout">
        <aside className="admin-sidebar" aria-label="관리 메뉴">
          <div className="admin-menu-title">관리 메뉴</div>
          <nav className="admin-nav">
            <button className={adminView === "submissions" ? "active" : ""} type="button" onClick={() => selectAdminView("submissions")}>제출 목록</button>
            <button className={adminView === "invites" ? "active" : ""} type="button" onClick={() => selectAdminView("invites")}>초대 링크</button>
            <button className={adminView === "formConfig" ? "active" : ""} type="button" onClick={() => selectAdminView("formConfig")}>신청서 문항 설정</button>
            <button className={adminView === "categoryTaxonomy" ? "active" : ""} type="button" onClick={() => selectAdminView("categoryTaxonomy")}>카테고리 관리</button>
          </nav>
          <div className="admin-sidebar-note">
            <strong>인증 범위</strong>
            <span>Creatrip 관리자 권한이 있는 계정만 접근할 수 있습니다.</span>
            {auth.user.role === "SUPER_ADMIN" && (
              <SidebarTokenTools
                state={tokenState}
                onToggle={() => {
                  setTokenState((current) => ({ ...current, expanded: !current.expanded }));
                  if (!tokenState.expanded && tokenState.tokens.length === 0) void refreshSidebarTokens();
                }}
                onCreateAndCopy={copyToolboxToken}
                onCopyRaw={copyRawSidebarToken}
                onRevoke={revokeSidebarToken}
              />
            )}
          </div>
        </aside>

        <AdminToast toast={toast} onClose={() => setToast(null)} />

        <section className="admin-main">
          {adminView === "submissions" ? (
            <>
              <SummaryCards list={list} />
              <form className="admin-toolbar" onSubmit={submitSearch}>
                <label>
                  <span>검색</span>
                  <input value={queryDraft} onChange={(event) => setQueryDraft(event.target.value)} placeholder="상점명, 이메일, 임시 저장 ID" />
                </label>
                <label>
                  <span>카테고리</span>
                  <select value={categoryId} onChange={(event) => setCategoryId(event.target.value)}>
                    <option value="">전체 카테고리</option>
                    {CATEGORY_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </label>
                <button className="button primary" type="submit">조회</button>
              </form>

              <div className="admin-content-grid">
                <SubmissionList
                  items={list.items}
                  loading={list.loading}
                  selectedId={selectedId}
                  page={list.page}
                  totalPages={list.totalPages}
                  onSelect={setSelectedSubmission}
                  onPageChange={loadList}
                />
                <SubmissionDetailPanel detail={detail} onCopyLink={copySubmissionLink} onRequestRevision={handleRequestRevision} revisionCreating={revisionCreating} canCreatePartnership={auth.user.role === "ADMIN" || auth.user.role === "SUPER_ADMIN"} onCreatePartnership={handleCreatePartnership} onUpdateSpot={handleUpdateSpot} canCreatePartnerAccount={auth.user.role === "SUPER_ADMIN"} onCreatePartnerAccount={handleCreatePartnerAccount} canDeleteSubmission={auth.user.role === "SUPER_ADMIN"} submissionDeleting={submissionDeleting} onDeleteSubmission={handleDeleteSubmission} submissionDuplicating={submissionDuplicating} onDuplicateSubmission={handleDuplicateSubmission} />
              </div>
            </>
          ) : adminView === "invites" ? (
            <InviteListPanel state={inviteList} onPageChange={loadInvites} onCopyInvite={copyInviteLink} onDuplicateInvite={handleDuplicateInvite} duplicatingInviteId={duplicatingInviteId} canDeleteInvite={auth.user.role === "SUPER_ADMIN"} onDeleteInvite={handleDeleteInvite} deletingInviteId={deletingInviteId} />
          ) : adminView === "formConfig" ? (
            <FormConfigPanel />
          ) : (
            <CategoryTaxonomyPanel />
          )}

        </section>
      </section>
    </main>
  );
}

function AdminToast({ toast, onClose }: { toast: AdminToastState | null; onClose: () => void }) {
  if (!toast) return null;
  return (
    <div className={`admin-toast ${toast.kind}`} role={toast.kind === "error" ? "alert" : "status"}>
      <span>{toast.message}</span>
      <button type="button" onClick={onClose} aria-label="닫기">×</button>
    </div>
  );
}

function SidebarTokenTools({
  state,
  onToggle,
  onCreateAndCopy,
  onCopyRaw,
  onRevoke,
}: {
  state: SidebarTokenState;
  onToggle: () => void;
  onCreateAndCopy: () => void;
  onCopyRaw: () => void;
  onRevoke: (token: AdminApiToken) => void;
}) {
  return (
    <div className="admin-sidebar-token-tools">
      <button className="button secondary compact admin-sidebar-token-button" type="button" onClick={onCreateAndCopy} disabled={state.loading}>
        {state.loading ? "복사 중" : "Toolbox 토큰 복사"}
      </button>
      <button className="admin-sidebar-token-toggle" type="button" onClick={onToggle}>{state.expanded ? "토큰 관리 닫기" : "토큰 관리"}</button>
      {state.expanded && (
        <div className="admin-sidebar-token-panel">
          {state.rawToken && (
            <div className="admin-sidebar-token-secret">
              <strong>방금 발급된 토큰</strong>
              <code>{state.rawToken}</code>
              <button className="button secondary compact" type="button" onClick={onCopyRaw}>다시 복사</button>
            </div>
          )}
          <div className="admin-sidebar-token-list">
            {state.tokens.length === 0 ? <span>발급된 토큰이 없습니다.</span> : state.tokens.map((token) => (
              <div className={`admin-sidebar-token-row ${token.revokedAt ? "revoked" : ""}`} key={token.id}>
                <span>{token.name}</span>
                <small>{token.tokenPrefix}…{token.tokenLast4}</small>
                {token.revokedAt ? <em>폐기됨</em> : <button type="button" onClick={() => onRevoke(token)}>폐기</button>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function CategoryConfigColumnHeader({
  categoryId,
  categoryTaxonomy,
  requestCategoryTaxonomy,
  selected,
  onSelect,
}: {
  categoryId: Exclude<IntakeCategoryId, "">;
  categoryTaxonomy: CategoryTaxonomyConfig;
  requestCategoryTaxonomy: CategoryRequestTaxonomyState;
  selected: boolean;
  onSelect: () => void;
}) {
  const label = categoryLabel(categoryId, categoryTaxonomy);
  const requestPath = requestCategoryTaxonomy.status === "ready" && getAllTaxonomyCategoryIds(requestCategoryTaxonomy.config).includes(categoryId)
    ? getCategoryPath(categoryId, requestCategoryTaxonomy.config)
    : null;
  const unavailableLabel = requestCategoryTaxonomy.status === "loading" ? "링크 확인 중" : requestCategoryTaxonomy.status === "error" ? "링크 확인 실패" : "미게시";
  return (
    <div className={`admin-config-cell head category-head ${selected ? "is-preview" : ""}`}>
      <button className="admin-category-preview-trigger" type="button" onClick={onSelect} aria-label={`${label} 설문지 미리보기`}>
        <span>{label}</span>
      </button>
      {requestPath ? (
        <a className="admin-category-request-link" href={requestPath} target="_blank" rel="noreferrer" aria-label={`${label} 요청 링크 새 창에서 열기`}>
          요청 링크 ↗
        </a>
      ) : (
        <span className="admin-category-request-unavailable">{unavailableLabel}</span>
      )}
    </div>
  );
}

function FormConfigPanel() {
  const [config, setConfig] = useState<FormConfigVersion>(() => loadFormConfigDraft());
  const [categoryTaxonomy, setCategoryTaxonomy] = useState<CategoryTaxonomyConfig>(DEFAULT_CATEGORY_TAXONOMY_CONFIG);
  const [publishedCategoryTaxonomy, setPublishedCategoryTaxonomy] = useState<CategoryRequestTaxonomyState>({ status: "loading" });
  const [previewCategoryId, setPreviewCategoryId] = useAdminSearchParam<Exclude<IntakeCategoryId, "">>("preview", "medical_dental");
  const [selectedCategoryGroupNodeId, setSelectedCategoryGroupNodeId] = useAdminSearchParam("group", () => loadStoredString(FORM_CONFIG_SELECTED_GROUP_STORAGE_KEY, "group.medical"));
  const [scopeType] = useState<EditorScopeType>("category");
  const [selectedTargetId, setSelectedTargetId] = useState<string>("field.reservation.useContactEmailForNotifications");
  const [search, setSearch] = useState("");
  const [selectedStepId, setSelectedStepId] = useAdminSearchParam("step", "company");
  const [previewToggles] = useState<PreviewToggles>(DEFAULT_PREVIEW_TOGGLES);
  const [importError, setImportError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [serverSaving, setServerSaving] = useState<"draft" | "publish" | null>(null);

  const activeCategoryTaxonomy = useMemo(() => normalizeCategoryTaxonomyConfig(categoryTaxonomy), [categoryTaxonomy]);
  const visibleCategoryGroups = useMemo(() => getVisibleCategoryGroups(activeCategoryTaxonomy), [activeCategoryTaxonomy]);
  const selectedCategoryGroup = visibleCategoryGroups.find((group) => group.nodeId === selectedCategoryGroupNodeId) ?? visibleCategoryGroups.find((group) => group.options.some((option) => option.id === previewCategoryId)) ?? visibleCategoryGroups[0];
  const allFormConfigCategories = useMemo(() => getAllTaxonomyCategoryIds(activeCategoryTaxonomy), [activeCategoryTaxonomy]);
  const catalog = useMemo(() => reconcileCatalogWithRuntime(config.catalog, createAllScenarioCatalog(allFormConfigCategories)), [config.catalog, allFormConfigCategories]);
  const scenarioData = useMemo(() => createScenarioData(previewCategoryId, previewToggles, config), [previewCategoryId, previewToggles, config]);
  const preview = useMemo(() => createFormConfigPreview(previewCategoryId, config, scenarioData, activeCategoryTaxonomy), [previewCategoryId, config, scenarioData, activeCategoryTaxonomy]);
  const scopeCondition = useMemo(() => createScopeCondition(scopeType, scenarioData), [scopeType, scenarioData]);
  const selectedTarget = catalog.find((item) => item.id === selectedTargetId) ?? catalog.find((item) => item.kind !== "option") ?? catalog[0];
  const selectedTargetRules = selectedTarget ? config.rules.filter((rule) => rule.targetId === selectedTarget.id) : [];
  const selectedRule = selectedTarget ? findEditorRule(config.rules, selectedTarget.id, scopeCondition) : null;
  const selectedEffect = selectedRule?.effect ?? {};
  const selectedOptions = selectedTarget ? catalog.filter((item) => item.kind === "option" && item.parentId === selectedTarget.id) : [];
  const filteredTargets = useMemo(() => filterCatalogTargets(catalog, search), [catalog, search]);
  const stepTargets = useMemo(() => catalog.filter((item) => item.kind === "step"), [catalog]);
  const orderedStepTargets = useMemo(() => orderStepTargets(stepTargets, preview.effective.steps), [stepTargets, preview.effective.steps]);
  const stepTargetParts = useMemo(() => groupStepTargetsByPart(orderedStepTargets), [orderedStepTargets]);
  const selectedPartIndex = getPartIndexForStepId(selectedStepId);
  const selectedPartStepTargets = stepTargetParts[selectedPartIndex]?.stepTargets ?? [];
  const matrixCategories = useMemo(() => selectedCategoryGroup?.options.map((option) => option.id) ?? [], [selectedCategoryGroup]);
  const matrixTargets = useMemo(() => buildStepMatrixTargets(catalog, selectedStepId, search), [catalog, selectedStepId, search]);
  const [expandedOptionParents, setExpandedOptionParents] = useState<Set<string>>(new Set());
  const matrixSearchActive = search.trim().length > 0;
  const matrixOptionCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const target of matrixTargets) {
      if (target.kind === "option" && target.parentId) counts.set(target.parentId, (counts.get(target.parentId) ?? 0) + 1);
    }
    return counts;
  }, [matrixTargets]);
  const visibleMatrixTargets = useMemo(() => {
    if (matrixSearchActive) return matrixTargets;
    return matrixTargets.filter((target) => target.kind !== "option" || (target.parentId && expandedOptionParents.has(target.parentId)));
  }, [matrixTargets, matrixSearchActive, expandedOptionParents]);
  function toggleOptionParent(parentId: string) {
    setExpandedOptionParents((current) => {
      const next = new Set(current);
      if (next.has(parentId)) next.delete(parentId);
      else next.add(parentId);
      return next;
    });
  }
  const matrixPreviewByCategory = useMemo(() => {
    return Object.fromEntries(matrixCategories.map((categoryId) => {
      const data = createScenarioData(categoryId, DEFAULT_PREVIEW_TOGGLES, config);
      return [categoryId, flattenEffectiveTargets(getEffectiveIntakeSchema(data, config).steps)];
    })) as Record<Exclude<IntakeCategoryId, "">, Map<string, TargetStatus>>;
  }, [config, matrixCategories]);
  const selectedPreviewStep = preview.effective.steps.find((step) => step.id === selectedStepId) ?? null;

  useEffect(() => {
    let alive = true;
    fetchAdminFormConfig<FormConfigVersion>()
      .then((state) => {
        if (!alive) return;
        const serverConfig = state.draft?.config ?? state.published?.config;
        if (serverConfig) setConfig(normalizeEditorConfig({ ...serverConfig, catalog: mergeFormCatalog(serverConfig.catalog ?? [], catalog) }));
      })
      .catch((error) => {
        if (alive) setImportError(errorMessage(error));
      });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    fetchAdminCategoryTaxonomy<CategoryTaxonomyConfig>()
      .then((state) => {
        if (!alive) return;
        const serverConfig = state.draft?.config ?? state.published?.config;
        if (serverConfig) setCategoryTaxonomy(normalizeCategoryTaxonomyConfig(serverConfig));
        setPublishedCategoryTaxonomy({ status: "ready", config: normalizeCategoryTaxonomyConfig(state.published?.config ?? DEFAULT_CATEGORY_TAXONOMY_CONFIG) });
      })
      .catch(() => setPublishedCategoryTaxonomy({ status: "error" }));
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    localStorage.setItem(FORM_CONFIG_DRAFT_STORAGE_KEY, JSON.stringify({ ...config, catalog }));
  }, [config, catalog]);

  useEffect(() => {
    if (!visibleCategoryGroups.length) return;
    if (!selectedCategoryGroup) setSelectedCategoryGroupNodeId(visibleCategoryGroups[0].nodeId);
  }, [visibleCategoryGroups, selectedCategoryGroup]);

  useEffect(() => {
    if (!selectedCategoryGroup) return;
    localStorage.setItem(FORM_CONFIG_SELECTED_GROUP_STORAGE_KEY, selectedCategoryGroup.nodeId);
    if (!selectedCategoryGroup.options.some((option) => option.id === previewCategoryId) && selectedCategoryGroup.options[0]) {
      setPreviewCategoryId(selectedCategoryGroup.options[0].id);
    }
  }, [selectedCategoryGroup, previewCategoryId]);

  useEffect(() => {
    if (!orderedStepTargets.some((target) => target.id === `step.${selectedStepId}`) && orderedStepTargets[0]) {
      setSelectedStepId(orderedStepTargets[0].id.replace(/^step\./, ""));
    }
  }, [orderedStepTargets, selectedStepId]);

  useEffect(() => {
    const handleStepArrowKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      if (isTextEntryTarget(event.target)) return;
      event.preventDefault();
      setSelectedStepId((current) => nextStepId(current, orderedStepTargets, event.key === "ArrowRight" ? 1 : -1));
    };
    window.addEventListener("keydown", handleStepArrowKey);
    return () => window.removeEventListener("keydown", handleStepArrowKey);
  }, [orderedStepTargets]);

  function updateConfig(nextConfig: FormConfigVersion) {
    setConfig({ ...nextConfig, status: "draft", catalog: mergeFormCatalog(nextConfig.catalog, catalog) });
    setNotice(null);
  }

  async function publishToServer() {
    setServerSaving("publish");
    setImportError(null);
    try {
      const nextConfig: FormConfigVersion = { ...config, status: "published", catalog, rules: sanitizeRuleOptionIds(config.rules, catalog) };
      const published = await publishAdminFormConfig<FormConfigVersion>(nextConfig);
      setConfig(normalizeEditorConfig({ ...published.config, catalog: mergeFormCatalog(published.config.catalog ?? [], catalog) }));
      setNotice(`저장됐어요 (${formatDate(published.updatedAt)}). 지금부터 파트너 신청서에 바로 적용됩니다.`);
    } catch (error) {
      setImportError(errorMessage(error));
    } finally {
      setServerSaving(null);
    }
  }

  function updateSelectedEffect(patch: Partial<FormRuleEffect>) {
    if (!selectedTarget) return;
    updateTargetEffect(selectedTarget, scopeCondition, patch, scopeLabel(scopeType, scenarioData));
  }

  function updateTargetEffect(target: FormCatalogItem, condition: FormRuleCondition, patch: Partial<FormRuleEffect>, label: string, keepEmptyKeys: Array<keyof FormRuleEffect> = []) {
    const existingRule = findEditorRule(config.rules, target.id, condition);
    const nextEffect = cleanEffect({ ...(existingRule?.effect ?? {}), ...patch }, keepEmptyKeys);
    const ruleId = editorRuleId(target.id, condition);
    const nextRules = config.rules.filter((rule) => rule.id !== ruleId);
    if (Object.keys(nextEffect).length > 0) {
      nextRules.push({
        id: ruleId,
        targetId: target.id,
        targetKind: target.kind,
        condition,
        effect: nextEffect,
        reason: `${label} 범위에서 ${target.label} 설정을 Admin에서 수정`,
        priority: 100,
      });
    }
    updateConfig({ ...config, rules: nextRules });
  }

  function quickCycleTarget(target: FormCatalogItem, categoryId: Exclude<IntakeCategoryId, "">, status: TargetStatus | undefined) {
    if (target.kind === "option") {
      quickToggleOptionTarget(target, categoryId, Boolean(status));
      return;
    }
    const condition: FormRuleCondition = { categoryId };
    const existingEffect = findEditorRule(config.rules, target.id, condition)?.effect ?? {};
    const preservedTextEffects = cleanEffect({
      label: existingEffect.label,
      title: existingEffect.title,
      description: existingEffect.description,
      helperText: existingEffect.helperText,
      placeholder: existingEffect.placeholder,
      checkboxText: existingEffect.checkboxText,
      defaultValue: existingEffect.defaultValue,
      allowedOptionIds: existingEffect.allowedOptionIds,
      addLabel: existingEffect.addLabel,
      minItems: existingEffect.minItems,
    });
    const nextEffect = { ...preservedTextEffects, ...nextQuickEffect(target, status) };
    updateTargetEffect(target, condition, nextEffect, categoryLabel(categoryId, activeCategoryTaxonomy));
  }

  function quickToggleOptionTarget(optionTarget: FormCatalogItem, categoryId: Exclude<IntakeCategoryId, "">, currentlyVisible: boolean) {
    if (!optionTarget.parentId) return;
    const parentTarget = catalog.find((item) => item.id === optionTarget.parentId);
    if (!parentTarget) return;
    const siblingOptions = catalog.filter((item) => item.kind === "option" && item.parentId === optionTarget.parentId);
    const currentVisibleIds = siblingOptions.filter((option) => matrixPreviewByCategory[categoryId].has(option.id)).map((option) => option.id);
    const nextVisibleIds = currentlyVisible
      ? currentVisibleIds.filter((optionId) => optionId !== optionTarget.id)
      : [...new Set([...currentVisibleIds, optionTarget.id])];
    const condition: FormRuleCondition = { categoryId };
    const existingEffect = findEditorRule(config.rules, parentTarget.id, condition)?.effect ?? {};
    const preservedTextEffects = cleanEffect({
      label: existingEffect.label,
      title: existingEffect.title,
      description: existingEffect.description,
      helperText: existingEffect.helperText,
      placeholder: existingEffect.placeholder,
      checkboxText: existingEffect.checkboxText,
      defaultValue: existingEffect.defaultValue,
      addLabel: existingEffect.addLabel,
      minItems: existingEffect.minItems,
    });
    const nextEffect = nextVisibleIds.length > 0
      ? { ...preservedTextEffects, visible: true, allowedOptionIds: nextVisibleIds }
      : { ...preservedTextEffects, visible: false };
    updateTargetEffect(parentTarget, condition, nextEffect, categoryLabel(categoryId, activeCategoryTaxonomy));
  }

  return (
    <section className="admin-card admin-form-config-card">
      <div className="admin-card-header">
        <div>
          <h2>신청서 문항 설정</h2>
          <p>카테고리별로 파트너에게 보일 질문과 필수 여부를 설정합니다.</p>
        </div>
      </div>
      <div className="admin-detail-body">
        <AdminToast toast={importError ? { kind: "error", message: importError } : notice ? { kind: "success", message: notice } : null} onClose={() => { setImportError(null); setNotice(null); }} />
        <div className="admin-form-config-toolbar">
          <label>
            <span>카테고리</span>
            <select value={selectedCategoryGroup?.nodeId ?? ""} onChange={(event) => setSelectedCategoryGroupNodeId(event.target.value)}>
              {visibleCategoryGroups.map((group) => <option key={group.nodeId} value={group.nodeId}>{group.label}</option>)}
            </select>
          </label>
        </div>

        <div className="admin-intake-part-navigation">
          <div className="admin-intake-part-tabs" role="group" aria-label="신청서 파트 선택">
            {stepTargetParts.map(({ part, stepTargets }, partIndex) => {
              const isSelected = partIndex === selectedPartIndex;
              const firstStepId = stepTargets[0]?.id.replace(/^step\./, "");
              return (
                <button
                  className={isSelected ? "active" : ""}
                  key={part.id}
                  type="button"
                  aria-pressed={isSelected}
                  disabled={!firstStepId}
                  onClick={() => firstStepId && setSelectedStepId(firstStepId)}
                >
                  <span>{part.label}</span>
                  <strong>{part.title}</strong>
                </button>
              );
            })}
          </div>
          <label className="admin-intake-step-select">
            <span>세부 항목</span>
            <select value={selectedStepId} onChange={(event) => setSelectedStepId(event.target.value)}>
              {selectedPartStepTargets.map((stepTarget) => <option key={stepTarget.id} value={stepTarget.id.replace(/^step\./, "")}>{stepTarget.label}</option>)}
            </select>
          </label>
        </div>

        <div className="admin-config-actions simple">
          <button className="button primary compact" type="button" onClick={publishToServer} disabled={Boolean(serverSaving)}>{serverSaving === "publish" ? "저장 중" : "저장"}</button>
        </div>

        <div className="admin-config-grid">
          <section className="admin-config-section">
            <div className="admin-config-matrix-scroll">
              <div className="admin-config-matrix full" role="group" aria-label="카테고리별 신청서 문항 설정표" style={{ gridTemplateColumns: `minmax(320px, 1.4fr) repeat(${matrixCategories.length}, minmax(112px, 1fr))` }}>
                <div className="admin-config-cell head">질문/항목</div>
                {matrixCategories.map((categoryId) => (
                  <CategoryConfigColumnHeader
                    key={categoryId}
                    categoryId={categoryId}
                    categoryTaxonomy={activeCategoryTaxonomy}
                    requestCategoryTaxonomy={publishedCategoryTaxonomy}
                    selected={categoryId === previewCategoryId}
                    onSelect={() => setPreviewCategoryId(categoryId)}
                  />
                ))}
                {visibleMatrixTargets.flatMap((target, targetIndex) => {
                  const rowClass = matrixRowClass(visibleMatrixTargets, targetIndex, selectedStepId);
                  const optionCount = matrixOptionCounts.get(target.id) ?? 0;
                  const optionsExpanded = expandedOptionParents.has(target.id);
                  return [
                    <div className={`admin-config-cell target ${rowClass}`} key={`${target.id}-target`}>
                      <strong>{matrixTargetLabel(target)}</strong>
                      {target.checkboxText && <small className="admin-target-copy">{target.checkboxText}</small>}
                      {optionCount > 0 && !matrixSearchActive && (
                        <button type="button" className="admin-option-toggle" onClick={() => toggleOptionParent(target.id)} aria-expanded={optionsExpanded}>
                          {optionsExpanded ? `하위 항목 ${optionCount}개 접기 ▴` : `하위 항목 ${optionCount}개 펼치기 ▾`}
                        </button>
                      )}
                    </div>,
                    ...matrixCategories.map((categoryId) => {
                      const status = matrixPreviewByCategory[categoryId].get(target.id);
                      const cellText = status ? formatTargetStatus(target, status) : "숨김";
                      if (target.id === "field.contract.upsellCommissionRate") {
                        const ruleRate = findEditorRule(config.rules, target.id, { categoryId })?.effect.defaultValue;
                        return (
                          <div className={`admin-config-cell quick rate-cell ${rowClass} ${categoryId === previewCategoryId ? "is-preview" : ""}`} key={`${target.id}-${categoryId}`}>
                            <button type="button" onClick={() => quickCycleTarget(target, categoryId, status)}>
                              <span className={quickPillClass(target, status)}>{cellText}</span>
                            </button>
                            {status && (
                              <label className="admin-rate-cell-input">
                                <span>기본값</span>
                                <input
                                  type="number"
                                  min={1}
                                  max={100}
                                  step={0.5}
                                  placeholder="10"
                                  value={ruleRate === undefined ? "" : String(ruleRate)}
                                  aria-label={`${categoryLabel(categoryId, activeCategoryTaxonomy)} 기본 업셀링 수수료율`}
                                  onChange={(event) => updateTargetEffect(target, { categoryId }, { defaultValue: event.target.value }, categoryLabel(categoryId, activeCategoryTaxonomy))}
                                />
                                <em>%</em>
                              </label>
                            )}
                          </div>
                        );
                      }
                      if (target.id === CONSULTATION_PRODUCT_TARGET_ID) {
                        return (
                          <ConsultationSeedCell
                            key={`${target.id}-${categoryId}`}
                            categoryId={categoryId}
                            categoryName={categoryLabel(categoryId, activeCategoryTaxonomy)}
                            className={`${rowClass} ${categoryId === previewCategoryId ? "is-preview" : ""}`}
                            rule={findEditorRule(config.rules, target.id, { categoryId })}
                            config={config}
                            onChange={(patch) => updateTargetEffect(target, { categoryId }, patch, categoryLabel(categoryId, activeCategoryTaxonomy), ["defaultValue"])}
                          />
                        );
                      }
                      return (
                        <button className={`admin-config-cell quick ${rowClass} ${categoryId === previewCategoryId ? "is-preview" : ""}`} type="button" key={`${target.id}-${categoryId}`} onClick={() => quickCycleTarget(target, categoryId, status)}>
                          <span className={quickPillClass(target, status)}>{cellText}</span>
                        </button>
                      );
                    }),
                  ];
                })}
              </div>
            </div>
          </section>

          <section className="admin-config-section admin-user-preview-rail">
            <div className="admin-json-header">
              <div>
                <h3>신청서 미리보기</h3>
                <p>{preview.label} 파트너에게 보이는 현재 단계 화면입니다.</p>
              </div>
            </div>
            <UserSurveyStepPreview step={selectedPreviewStep} selectedStepId={selectedStepId} categoryLabelText={preview.label} />
          </section>
        </div>

      </div>
    </section>
  );
}

function loadCategoryTaxonomyDraft(): CategoryTaxonomyConfig {
  try {
    if (typeof window === "undefined") return DEFAULT_CATEGORY_TAXONOMY_CONFIG;
    const raw = localStorage.getItem(CATEGORY_TAXONOMY_DRAFT_STORAGE_KEY);
    if (!raw) return DEFAULT_CATEGORY_TAXONOMY_CONFIG;
    const parsed = JSON.parse(raw) as CategoryTaxonomyConfig;
    if (!parsed.versionId || !Array.isArray(parsed.nodes)) return DEFAULT_CATEGORY_TAXONOMY_CONFIG;
    return normalizeCategoryTaxonomyConfig(parsed);
  } catch {
    return DEFAULT_CATEGORY_TAXONOMY_CONFIG;
  }
}

function CategoryTaxonomyPanel() {
  const [config, setConfig] = useState<CategoryTaxonomyConfig>(() => loadCategoryTaxonomyDraft());
  const [selectedNodeId, setSelectedNodeId] = useAdminSearchParam("node", () => config.nodes.find((node) => node.kind === "category")?.id ?? config.nodes[0]?.id ?? "");
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [serverSaving, setServerSaving] = useState<"draft" | "publish" | null>(null);
  const [savedState, setSavedState] = useState<SavedTaxonomyState>({ status: "loading" });
  const normalizedConfig = useMemo(() => normalizeCategoryTaxonomyConfig(config), [config]);
  const unsaved = useMemo(() => hasUnsavedTaxonomyChanges(savedState, normalizedConfig), [savedState, normalizedConfig]);
  const groups = useMemo(() => getVisibleCategoryGroups(normalizedConfig), [normalizedConfig]);
  const diagnostics = useMemo(() => validateCategoryTaxonomy(normalizedConfig), [normalizedConfig]);
  const rawSelectedNode = getTaxonomyNode(normalizedConfig, selectedNodeId);
  const selectedNode = rawSelectedNode && rawSelectedNode.status !== "deprecated" ? rawSelectedNode : normalizedConfig.nodes.find((node) => node.status !== "deprecated") ?? null;
  const groupNodes = normalizedConfig.nodes.filter((node) => node.kind === "group" && node.status !== "deprecated");

  useEffect(() => {
    let alive = true;
    fetchAdminCategoryTaxonomy<CategoryTaxonomyConfig>()
      .then((state) => {
        if (!alive) return;
        const published = state.published;
        if (!published) {
          setSavedState({ status: "missing" });
          return;
        }
        const serverConfig = normalizeCategoryTaxonomyConfig(published.config);
        setConfig(serverConfig);
        setSavedState({ status: "saved", config: serverConfig, updatedAt: published.updatedAt });
      })
      .catch((loadError) => { if (alive) setError(errorMessage(loadError)); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    localStorage.setItem(CATEGORY_TAXONOMY_DRAFT_STORAGE_KEY, JSON.stringify(normalizedConfig));
  }, [normalizedConfig]);

  useEffect(() => {
    if (!unsaved) return;
    const warnBeforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [unsaved]);

  function updateConfig(nextConfig: CategoryTaxonomyConfig) {
    setConfig({ ...normalizeCategoryTaxonomyConfig(nextConfig), status: "draft" });
    setNotice(null);
  }

  function updateNode(nodeId: string, patch: Partial<CategoryTaxonomyNode>) {
    updateConfig({
      ...normalizedConfig,
      nodes: sortTaxonomyNodes(normalizedConfig.nodes.map((node) => (node.id === nodeId ? { ...node, ...patch } : node))),
    });
  }

  function addCategoryToGroup(group: CategoryTaxonomyNode) {
    const domain = inferDomainForGroup(group, normalizedConfig);
    const profileId = createCustomProfileId(domain, normalizedConfig);
    const siblingOrders = normalizedConfig.nodes.filter((node) => node.kind === "category" && node.parentId === group.id).map((node) => node.order);
    const nextNode: CategoryTaxonomyNode = {
      id: `category.${profileId}`,
      kind: "category",
      parentId: group.id,
      profileId,
      label: "새 카테고리",
      description: "새로 추가한 카테고리입니다. 파트너에게 보일 설명을 입력해주세요.",
      slug: `new-category-${siblingOrders.length + 1}`,
      aliases: [],
      examples: [],
      order: (Math.max(-1, ...siblingOrders) + 1),
      status: "active",
    };
    setConfig({ ...normalizedConfig, status: "draft", nodes: sortTaxonomyNodes([...normalizedConfig.nodes, nextNode]) });
    setSelectedNodeId(nextNode.id);
    setNotice(null);
  }

  function deleteSelectedTree() {
    if (!selectedNode) return;
    const deletingGroup = selectedNode.kind === "group";
    const deleteIds = new Set([
      selectedNode.id,
      ...(deletingGroup ? normalizedConfig.nodes.filter((node) => node.parentId === selectedNode.id).map((node) => node.id) : []),
    ]);
    const message = deletingGroup
      ? `${selectedNode.label} 그룹과 그 안의 카테고리를 목록에서 삭제할까요?`
      : `${selectedNode.label} 카테고리를 목록에서 삭제할까요?`;
    if (!window.confirm(message)) return;
    const nextNodes = sortTaxonomyNodes(normalizedConfig.nodes.map((node) => deleteIds.has(node.id) ? { ...node, status: "deprecated" as const } : node));
    const nextSelected = nextNodes.find((node) => node.status !== "deprecated" && node.kind === "category") ?? nextNodes.find((node) => node.status !== "deprecated");
    setConfig({ ...normalizedConfig, status: "draft", nodes: nextNodes });
    setSelectedNodeId(nextSelected?.id ?? "");
    setNotice(null);
  }

  async function publishToServer() {
    setServerSaving("publish");
    setError(null);
    try {
      const blocking = diagnostics.filter((diagnostic) => diagnostic.level === "error");
      if (blocking.length) throw new Error(formatCategoryTaxonomyBlockingMessage(blocking));
      const published = await publishAdminCategoryTaxonomy<CategoryTaxonomyConfig>({ ...normalizedConfig, status: "published" });
      const publishedConfig = normalizeCategoryTaxonomyConfig(published.config);
      setConfig(publishedConfig);
      setSavedState({ status: "saved", config: publishedConfig, updatedAt: published.updatedAt });
      setNotice(`저장됐어요 (${formatDate(published.updatedAt)}). 지금부터 파트너 신청서와 폼 설정 화면에 바로 적용됩니다.`);
    } catch (publishError) {
      setError(errorMessage(publishError));
    } finally {
      setServerSaving(null);
    }
  }

  return (
    <section className="admin-card admin-form-config-card">
      <div className="admin-card-header">
        <div>
          <h2>카테고리 관리</h2>
          <p>파트너가 신청서에서 선택하는 카테고리 이름, 공개 여부, 순서를 관리합니다.</p>
        </div>
      </div>
      <div className="admin-detail-body">
        <AdminToast toast={error ? { kind: "error", message: error } : notice ? { kind: "success", message: notice } : null} onClose={() => { setError(null); setNotice(null); }} />

        <div className="admin-config-actions simple">
          <button className="button primary compact" type="button" onClick={publishToServer} disabled={Boolean(serverSaving)}>{serverSaving === "publish" ? "저장 중" : "저장"}</button>
          {unsaved && <span className="admin-save-state unsaved" role="status">저장하지 않은 변경사항이 있어요. 저장해야 파트너에게 보이는 카테고리에 반영됩니다.</span>}
          {!unsaved && savedState.status === "saved" && <span className="admin-save-state" role="status">마지막 저장 {formatDate(savedState.updatedAt)}</span>}
        </div>

        <div className="admin-taxonomy-layout">
          <section className="admin-config-section admin-taxonomy-tree-section">
            <div className="admin-json-header">
              <div>
                <h3>카테고리 목록</h3>
                <p>카테고리를 선택해 오른쪽에서 수정합니다.</p>
              </div>
            </div>
            <div className="admin-taxonomy-tree">
              {groupNodes.map((group) => {
                const children = normalizedConfig.nodes.filter((node) => node.kind === "category" && node.parentId === group.id && node.status !== "deprecated").sort((left, right) => left.order - right.order || left.label.localeCompare(right.label));
                return (
                  <div className="admin-taxonomy-group" key={group.id}>
                    <button className={selectedNode?.id === group.id ? "active" : ""} type="button" onClick={() => setSelectedNodeId(group.id)}>
                      <span>{statusLabel(group.status)}</span>
                      <strong>{group.label}</strong>
                      <small>/{group.slug}</small>
                    </button>
                    <div className="admin-taxonomy-children">
                      {children.map((child) => (
                        <button className={selectedNode?.id === child.id ? "active" : ""} type="button" key={child.id} onClick={() => setSelectedNodeId(child.id)}>
                          <span>{statusLabel(child.status)}</span>
                          <strong>{child.label}</strong>
                          <small>/{child.slug}</small>
                        </button>
                      ))}
                      <button className="admin-taxonomy-add-button" type="button" onClick={() => addCategoryToGroup(group)}>+ 카테고리 추가</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="admin-config-section admin-taxonomy-editor-section">
            <div className="admin-json-header">
              <div>
                <h3>{selectedNode?.kind === "group" ? "그룹 정보" : "카테고리 정보"}</h3>
                <p>{selectedNode ? selectedNode.label : "수정할 카테고리를 선택해주세요."}</p>
              </div>
            </div>
            {selectedNode && (
              <div className="admin-rule-editor admin-taxonomy-editor">
                <div className="admin-rule-grid">
                  <label>
                    <span>라벨</span>
                    <input value={selectedNode.label} onChange={(event) => updateNode(selectedNode.id, { label: event.target.value })} />
                  </label>
                  <label>
                    <span>URL 이름</span>
                    <input value={selectedNode.slug} onChange={(event) => updateNode(selectedNode.id, { slug: slugify(event.target.value) })} />
                  </label>
                  <label>
                    <span>공개 여부</span>
                    <select value={selectedNode.status === "active" ? "active" : "hidden"} onChange={(event) => updateNode(selectedNode.id, { status: event.target.value as CategoryTaxonomyNodeStatus })}>
                      <option value="active">공개</option>
                      <option value="hidden">비공개</option>
                    </select>
                  </label>
                  <label>
                    <span>순서</span>
                    <input type="number" min={0} value={selectedNode.order} onChange={(event) => updateNode(selectedNode.id, { order: Number(event.target.value) || 0 })} />
                  </label>
                  {selectedNode.kind === "category" && (
                    <label>
                      <span>상위 그룹</span>
                      <select value={selectedNode.parentId ?? ""} onChange={(event) => updateNode(selectedNode.id, { parentId: event.target.value })}>
                        {groupNodes.map((group) => <option key={group.id} value={group.id}>{group.label}</option>)}
                      </select>
                    </label>
                  )}
                  <label className="wide">
                    <span>설명</span>
                    <textarea rows={3} value={selectedNode.description} onChange={(event) => updateNode(selectedNode.id, { description: event.target.value })} />
                  </label>
                  {selectedNode.kind === "category" && (
                    <>
                      <label className="wide">
                        <span>예시</span>
                        <textarea rows={2} value={(selectedNode.examples ?? []).join("\n")} onChange={(event) => updateNode(selectedNode.id, { examples: splitLines(event.target.value) })} />
                      </label>
                    </>
                  )}
                </div>
                <div className="admin-config-actions simple">
                  <button className="button danger compact" type="button" onClick={deleteSelectedTree}>{selectedNode.kind === "group" ? "그룹 삭제" : "카테고리 삭제"}</button>
                </div>
              </div>
            )}
          </section>

          <section className="admin-config-section admin-taxonomy-preview-section">
            <div className="admin-json-header">
              <div>
                <h3>신청서 미리보기</h3>
                <p>파트너에게 공개되는 카테고리 목록입니다.</p>
              </div>
            </div>
            <div className="admin-taxonomy-public-preview">
              {groups.map((group) => (
                <article key={group.nodeId}>
                  <strong>{group.label}</strong>
                  <small>/{group.slug}</small>
                  <p>{group.description}</p>
                  <div>
                    {group.options.map((option) => <span key={option.nodeId}>{option.label}</span>)}
                  </div>
                </article>
              ))}
            </div>
          </section>
        </div>
      </div>
    </section>
  );
}

function ConsultationSeedCell({
  categoryId,
  categoryName,
  className,
  rule,
  config,
  onChange,
}: {
  categoryId: Exclude<IntakeCategoryId, "">;
  categoryName: string;
  className: string;
  rule: FormRule | null;
  config: FormConfigVersion;
  onChange: (patch: Partial<FormRuleEffect>) => void;
}) {
  const profile = getCategoryProfile(categoryId);
  const categoryDefaultName = profile?.consultationProduct.optionName ?? "";
  const mode = getConsultationSeedMode(rule?.effect.defaultValue);
  const optionName = mode === "custom" ? String(rule?.effect.defaultValue ?? "") : "";
  const supportsSeed = isUpsellContractType(getDefaultContractTypeForContext(categoryId, profile?.domain ?? "", config));

  return (
    <div className={`admin-config-cell quick consultation-cell ${className}`}>
      <select
        value={mode}
        aria-label={`${categoryName} 상담 상품 자동 입력`}
        onChange={(event) => onChange(createConsultationSeedEffect(event.target.value as ConsultationSeedMode, optionName, categoryDefaultName))}
      >
        <option value="default">기본 문구</option>
        <option value="custom">직접 입력</option>
        <option value="none">사용 안 함</option>
      </select>
      {mode === "custom" && (
        <input
          value={optionName}
          placeholder={categoryDefaultName}
          aria-label={`${categoryName} 상담 상품 옵션명`}
          onChange={(event) => onChange(createConsultationSeedEffect("custom", event.target.value, categoryDefaultName))}
        />
      )}
      {mode !== "none" && <small>{mode === "custom" ? optionName || categoryDefaultName : categoryDefaultName}</small>}
      {!supportsSeed && <small className="admin-consultation-note">무료예약 계약에서만 자동 입력됩니다</small>}
    </div>
  );
}

function AdminLoading() {
  return (
    <main className="admin-login-screen">
      <section className="admin-login-card">
        <p className="eyebrow">Onboarding admin</p>
        <h1>로그인 상태를 확인하고 있습니다</h1>
      </section>
    </main>
  );
}

function AdminLogin({ onLogin }: { onLogin: (user: AdminUser) => void }) {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (challengeId) {
        onLogin(await loginAdminWithTwoFactor(challengeId, code));
      } else {
        const result = await loginAdmin(identifier, password);
        if ("challengeId" in result) {
          setChallengeId(result.challengeId);
          setCode("");
        } else {
          onLogin(result);
        }
      }
    } catch (loginError) {
      if (challengeId && loginError instanceof AdminApiError && loginError.status === 410) {
        // challenge가 만료되면 아이디와 비밀번호 단계로 돌아간다.
        setChallengeId(null);
        setError("인증 시간이 지났습니다. 다시 로그인해 주세요.");
      } else if (challengeId && loginError instanceof AdminApiError && loginError.status === 401) {
        setError("인증 코드를 확인하거나 잠시 후 다시 시도해 주세요.");
      } else {
        setError(errorMessage(loginError));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="admin-login-screen">
      <section className="admin-login-card">
        <div className="admin-brand-block">
          <div className="admin-mark" aria-hidden="true">OB</div>
          <div>
            <h1>Onboarding admin</h1>
            <p>Creatrip admin 계정으로 로그인하세요.</p>
          </div>
        </div>
        {error && <div className="admin-alert error" role="alert">{error}</div>}
        <form className="admin-login-form" onSubmit={handleSubmit}>
          {challengeId ? (
            <label>
              <span>인증 코드</span>
              <input value={code} onChange={(event) => setCode(event.target.value)} autoComplete="one-time-code" placeholder="인증 앱 코드 또는 복구 코드" required />
            </label>
          ) : (
            <>
              <label>
                <span>ID 또는 이메일</span>
                <input value={identifier} onChange={(event) => setIdentifier(event.target.value)} autoComplete="username" required />
              </label>
              <label>
                <span>비밀번호</span>
                <input value={password} onChange={(event) => setPassword(event.target.value)} type="password" autoComplete="current-password" required />
              </label>
            </>
          )}
          <button className="button primary" type="submit" disabled={submitting || (challengeId ? !code.trim() : !identifier || !password)}>{submitting ? "로그인 중" : "로그인"}</button>
        </form>
        <p className="admin-helper-text">Creatrip 관리자 권한이 있는 계정만 접근할 수 있습니다.</p>
      </section>
    </main>
  );
}

function SummaryCards({ list }: { list: ListState }) {
  const adminAccountRequests = list.items.filter((item) => item.adminAccountNeeded && item.adminLoginId).length;
  const fileCount = list.items.reduce((sum, item) => sum + item.fileCount, 0);
  return (
    <section className="admin-summary-grid" aria-label="제출 요약">
      <div><span>검색 결과</span><strong>{list.total.toLocaleString()}</strong></div>
      <div><span>현재 페이지</span><strong>{list.items.length.toLocaleString()}</strong></div>
      <div><span>Admin 계정 요청</span><strong>{adminAccountRequests.toLocaleString()}</strong></div>
      <div><span>첨부 파일</span><strong>{fileCount.toLocaleString()}</strong></div>
    </section>
  );
}

function SubmissionList({ items, loading, selectedId, page, totalPages, onSelect, onPageChange }: {
  items: AdminSubmissionSummary[];
  loading: boolean;
  selectedId: string | null;
  page: number;
  totalPages: number;
  onSelect: (id: string) => void;
  onPageChange: (page: number) => void;
}) {
  return (
    <section className="admin-card admin-list-card">
      <div className="admin-card-header">
        <div>
          <h2>제출 목록</h2>
          <p>{loading ? "불러오는 중" : `${items.length}건 표시 중`}</p>
        </div>
        <div className="admin-pager">
          <button className="button compact" type="button" onClick={() => onPageChange(page - 1)} disabled={page <= 1 || loading}>이전</button>
          <span>{page} / {totalPages}</span>
          <button className="button compact" type="button" onClick={() => onPageChange(page + 1)} disabled={page >= totalPages || loading}>다음</button>
        </div>
      </div>
      <div className="admin-table-head" aria-hidden="true"><span>상점명</span><span>카테고리</span><span>제출일</span><span>첨부</span></div>
      <div className="admin-submission-list">
        {!loading && items.length === 0 && <div className="admin-empty">조건에 맞는 제출 정보가 없습니다.</div>}
        {items.map((item) => (
          <a
            aria-current={selectedId === item.id ? "page" : undefined}
            className={`admin-submission-row ${selectedId === item.id ? "selected" : ""}`}
            href={getAdminSubmissionPath(item.id)}
            key={item.id}
            onClick={(event) => {
              if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
              event.preventDefault();
              onSelect(item.id);
            }}
          >
            <span>
              <strong>
                {item.storeNameKo || "상점명 없음"}
                {item.googleIntegrations.length > 0 && !item.spotId && <span className="admin-google-badge" title={`${googleIntegrationLabel(item.googleIntegrations)} 요청 · 스팟 번호 입력 전`}>G 연동</span>}
              </strong>
              <small>{item.contactEmail || item.draftId}</small>
            </span>
            <span>{item.category.label || item.category.categoryId || "미분류"}</span>
            <span>{formatDate(item.submittedAt)}</span>
            <span>{item.fileCount.toLocaleString()}개</span>
          </a>
        ))}
      </div>
    </section>
  );
}

function InviteListPanel({ state, onPageChange, onCopyInvite, onDuplicateInvite, duplicatingInviteId, canDeleteInvite, onDeleteInvite, deletingInviteId }: {
  state: InviteListState;
  onPageChange: (page: number) => void;
  onCopyInvite: (invite: AdminInviteSummary) => void;
  onDuplicateInvite: (invite: AdminInviteSummary) => void;
  duplicatingInviteId: string | null;
  canDeleteInvite: boolean;
  onDeleteInvite: (invite: AdminInviteSummary) => Promise<void>;
  deletingInviteId: string | null;
}) {
  const [deleteTarget, setDeleteTarget] = useState<AdminInviteSummary | null>(null);
  return (
    <section className="admin-card admin-list-card admin-invite-list-card">
      <div className="admin-card-header">
        <div>
          <h2>초대 링크</h2>
          <p>{state.loading ? "불러오는 중" : `${state.total.toLocaleString()}건 중 ${state.items.length.toLocaleString()}건 표시`}</p>
        </div>
        <div className="admin-pager">
          <button className="button compact" type="button" onClick={() => onPageChange(state.page - 1)} disabled={state.page <= 1 || state.loading}>이전</button>
          <span>{state.page} / {state.totalPages}</span>
          <button className="button compact" type="button" onClick={() => onPageChange(state.page + 1)} disabled={state.page >= state.totalPages || state.loading}>다음</button>
        </div>
      </div>
      <div className="admin-table-head admin-invite-table-head" aria-hidden="true"><span>상점명</span><span>카테고리</span><span>상태</span><span>관리</span></div>
      <div className="admin-submission-list">
        {!state.loading && state.items.length === 0 && <div className="admin-empty">발급된 초대 링크가 없습니다.</div>}
        {state.items.map((invite) => (
          <div className="admin-submission-row admin-invite-row" key={invite.id}>
            <span><strong>{invite.storeNameKo || "상점명 없음"}</strong><small>{invite.draftId}</small></span>
            <span>{inviteCategoryLabel(invite.categoryId)}</span>
            <span>
              <strong>{invite.revisionRequested ? "보충 작성 중" : invite.submittedAt ? "제출 완료" : isInviteExpired(invite.expiresAt) ? "만료" : "작성 대기"}</strong>
              <small>{invite.revisionRequested ? `재요청 ${formatDate(invite.updatedAt)}` : invite.submittedAt ? formatDate(invite.submittedAt) : `만료 ${formatDate(invite.expiresAt)}`}</small>
            </span>
            <span className="admin-invite-actions">
              <button className="button secondary compact" type="button" onClick={() => onCopyInvite(invite)}>복사</button>
              <button className="button secondary compact" type="button" onClick={() => onDuplicateInvite(invite)} disabled={duplicatingInviteId !== null} title="이 초안과 첨부를 그대로 가져온 새 초대 링크를 만듭니다.">{duplicatingInviteId === invite.id ? "복제 중" : "복제"}</button>
              {canDeleteInvite && <button className="button danger compact" type="button" onClick={() => setDeleteTarget(invite)} disabled={deletingInviteId !== null} title="이 초대 링크와 작성 중인 초안, 첨부를 삭제합니다.">{deletingInviteId === invite.id ? "삭제 중" : "삭제"}</button>}
            </span>
          </div>
        ))}
      </div>

      {deleteTarget && (
        <DeleteConfirmDialog
          title="초대 링크 삭제"
          description={`${deleteTarget.storeNameKo || deleteTarget.draftId} 초대 링크와 작성 중인 신청 내용, 첨부 파일이 모두 삭제되며 되돌릴 수 없습니다. 파트너는 기존 링크를 더 이상 사용할 수 없습니다.`}
          confirmLabel={deleteTarget.storeNameKo || deleteTarget.draftId}
          submitting={deletingInviteId === deleteTarget.id}
          onClose={() => setDeleteTarget(null)}
          onConfirm={async () => {
            await onDeleteInvite(deleteTarget);
            setDeleteTarget(null);
          }}
        />
      )}
    </section>
  );
}

function SubmissionDetailPanel({ detail, onCopyLink, onRequestRevision, revisionCreating, canCreatePartnership, onCreatePartnership, onUpdateSpot, canCreatePartnerAccount, onCreatePartnerAccount, canDeleteSubmission, submissionDeleting, onDeleteSubmission, submissionDuplicating, onDuplicateSubmission }: { detail: DetailState; onCopyLink: (submissionId: string) => void; onRequestRevision: (input: RequestSubmissionRevisionInput) => Promise<void>; revisionCreating: boolean; canCreatePartnership: boolean; onCreatePartnership: () => Promise<void>; onUpdateSpot: (spotId: number | null) => Promise<void>; canCreatePartnerAccount: boolean; onCreatePartnerAccount: (input: PartnerAccountInput) => Promise<void>; canDeleteSubmission: boolean; submissionDeleting: boolean; onDeleteSubmission: () => Promise<void>; submissionDuplicating: boolean; onDuplicateSubmission: () => Promise<void> }) {
  const submission = detail.item;
  const sections = useMemo(() => submission ? buildDetailSections(submission) : [], [submission]);
  const fileGroups = useMemo(() => submission ? buildAdminFileGroups(submission) : [], [submission]);
  const [revisionDialogOpen, setRevisionDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reviewSheetOpen, setReviewSheetOpen] = useState(false);

  if (detail.loading) return <section className="admin-card admin-detail-card" aria-busy="true"><div className="admin-empty" role="status">상세 정보를 불러오는 중입니다.</div></section>;
  if (detail.error) return <section className="admin-card admin-detail-card"><div className="admin-alert error" role="alert">{detail.error}</div></section>;
  if (!submission) return <section className="admin-card admin-detail-card"><div className="admin-empty" role="status">제출 건을 선택해주세요.</div></section>;

  return (
    <section className="admin-card admin-detail-card" aria-labelledby="admin-submission-detail-heading">
      <p className="visually-hidden" role="status">{submission.storeNameKo || "상점명 없음"} 제출정보를 열었습니다.</p>
      <div className="admin-card-header">
        <div>
          <h2 id="admin-submission-detail-heading">{submission.storeNameKo || "상점명 없음"}</h2>
          <p>제출 ID {submission.id} · 임시 저장 ID {submission.draftId}</p>
        </div>
        <div className="admin-detail-header-actions">
          <button className="button secondary compact" type="button" onClick={() => setReviewSheetOpen(true)}>전체 화면 검수</button>
          <button className="button secondary compact" type="button" onClick={() => onCopyLink(submission.id)}>관리자 링크 복사</button>
          <button className="button secondary compact" type="button" onClick={() => setRevisionDialogOpen(true)} disabled={revisionCreating}>{revisionCreating ? "요청 중" : "수정 요청"}</button>
          <button className="button secondary compact" type="button" onClick={onDuplicateSubmission} disabled={submissionDuplicating} title="이 제출 내용과 첨부를 복사해 새 초대 링크를 만듭니다.">{submissionDuplicating ? "복제 중" : "복제"}</button>
          {canDeleteSubmission && <button className="button danger compact" type="button" onClick={() => setDeleteDialogOpen(true)} disabled={submissionDeleting}>{submissionDeleting ? "삭제 중" : "삭제"}</button>}
          <span className={`admin-badge ${submission.adminAccountNeeded && submission.adminLoginId ? "success" : "muted"}`}>{submission.adminAccountNeeded && submission.adminLoginId ? "관리자 계정 요청" : "계정 요청 없음"}</span>
        </div>
      </div>

      {reviewSheetOpen && <AdminSubmissionReviewSheet submission={submission} sections={sections} fileGroups={fileGroups} getFilePreviewPath={adminFilePreviewPath} onClose={() => setReviewSheetOpen(false)} />}

      {deleteDialogOpen && (
        <DeleteConfirmDialog
          title="제출 건 삭제"
          description={`${submission.storeNameKo || submission.id} 제출 내용과 버전 이력, 내부 작업 문서가 모두 삭제되며 되돌릴 수 없습니다. 첨부 파일과 파트너 초대 링크는 그대로 남아 같은 링크로 다시 제출받을 수 있습니다.`}
          confirmLabel={submission.storeNameKo || submission.id}
          submitting={submissionDeleting}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={async () => {
            await onDeleteSubmission();
            setDeleteDialogOpen(false);
          }}
        />
      )}

      {revisionDialogOpen && (
        <RevisionRequestDialog
          submissionLabel={submission.storeNameKo || submission.id}
          submitting={revisionCreating}
          onClose={() => setRevisionDialogOpen(false)}
          onSubmit={async (input) => {
            await onRequestRevision(input);
            setRevisionDialogOpen(false);
          }}
        />
      )}

      <div className="admin-detail-body">
        {(submission.googleIntegrations.length > 0 || submission.spotId) && (
          <SpotPanel key={`spot-${submission.id}`} submission={submission} onSave={onUpdateSpot} />
        )}

        {sections.map((section) => (
          <section className="admin-info-section" key={section.title}>
            <h3>{section.title}</h3>
            <div className="admin-kv-grid">
              {section.rows.map((row) => row.kind === "adminPassword"
                ? <AdminPasswordInfoRow key={`${submission.id}:${row.label}`} submissionId={submission.id} hasStoredPassword={submission.adminAccountNeeded && Boolean(row.value)} />
                : <InfoRow key={row.label} label={row.label} value={row.value} />)}
            </div>
          </section>
        ))}

        {canCreatePartnership && (submission.partnershipCode || submission.files.some((file) => file.category === "business_registration")) && (
          <PartnershipPanel key={`partnership-${submission.id}`} submission={submission} onCreate={onCreatePartnership} />
        )}

        {submission.adminAccountNeeded && canCreatePartnerAccount && <PartnerAccountPanel key={submission.id} submission={submission} onCreate={onCreatePartnerAccount} />}

        <AdminSubmissionFiles submission={submission} />

        <SubmissionDocumentPanel submissionId={submission.id} />
      </div>
    </section>
  );
}

export function DeleteConfirmDialog({ title, description, confirmLabel, submitting, onClose, onConfirm }: { title: string; description: string; confirmLabel: string; submitting: boolean; onClose: () => void; onConfirm: () => Promise<void> }) {
  const [typedLabel, setTypedLabel] = useState("");
  const confirmed = typedLabel.trim() === confirmLabel.trim();

  async function submitDeletion(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!confirmed) return;
    try {
      await onConfirm();
    } catch {
      // The parent displays the API error toast and keeps the dialog open for retry.
    }
  }

  return (
    <div className="admin-modal-backdrop" role="presentation">
      <section className="admin-revision-modal" role="dialog" aria-modal="true" aria-labelledby="admin-delete-title" aria-describedby="admin-delete-description">
        <form onSubmit={submitDeletion}>
          <div className="admin-revision-modal-head">
            <div>
              <h3 id="admin-delete-title">{title}</h3>
              <p id="admin-delete-description">{description}</p>
            </div>
            <button className="modal-close" type="button" onClick={onClose} disabled={submitting} aria-label="삭제 닫기">×</button>
          </div>

          <label className="admin-revision-reason-field">
            <span>확인을 위해 <strong>{confirmLabel}</strong> 을(를) 그대로 입력하세요.</span>
            <input value={typedLabel} onChange={(event) => setTypedLabel(event.target.value)} placeholder={confirmLabel} autoComplete="off" />
          </label>

          <div className="admin-revision-actions">
            <button className="button secondary" type="button" onClick={onClose} disabled={submitting}>취소</button>
            <button className="button danger" type="submit" disabled={submitting || !confirmed}>{submitting ? "삭제 중" : "영구 삭제"}</button>
          </div>
        </form>
      </section>
    </div>
  );
}

function RevisionRequestDialog({ submissionLabel, submitting, onClose, onSubmit }: { submissionLabel: string; submitting: boolean; onClose: () => void; onSubmit: (input: RequestSubmissionRevisionInput) => Promise<void> }) {
  const [reason, setReason] = useState("");
  const [rotateLink, setRotateLink] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const trimmedReason = reason.trim();

  async function submitRevisionRequest(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!trimmedReason) {
      setError("파트너에게 전달할 수정 요청 사유를 입력해주세요.");
      return;
    }
    setError(null);
    try {
      await onSubmit({ reason: trimmedReason, rotateLink });
    } catch {
      // The parent displays the API error toast and keeps the dialog open for retry.
    }
  }

  return (
    <div className="admin-modal-backdrop" role="presentation">
      <section className="admin-revision-modal" role="dialog" aria-modal="true" aria-labelledby="admin-revision-title" aria-describedby="admin-revision-description">
        <form onSubmit={submitRevisionRequest}>
          <div className="admin-revision-modal-head">
            <div>
              <h3 id="admin-revision-title">수정 요청 보내기</h3>
              <p id="admin-revision-description">{submissionLabel} 파트너가 다시 작성해야 하는 내용을 구체적으로 적어주세요. 기본값은 기존 파트너 링크를 그대로 다시 열고 복사합니다.</p>
            </div>
            <button className="modal-close" type="button" onClick={onClose} disabled={submitting} aria-label="수정 요청 닫기">×</button>
          </div>

          <label className="admin-revision-reason-field">
            <span>수정 요청 사유 <strong aria-hidden="true">*</strong></span>
            <textarea value={reason} onChange={(event) => { setReason(event.target.value); if (error) setError(null); }} rows={5} required placeholder="예: 가격표의 외국인 전용 옵션과 예약 가능 시간을 보완해주세요." />
          </label>
          {error && <div className="admin-alert error" role="alert">{error}</div>}

          <label className="admin-revision-security-option">
            <input type="checkbox" checked={rotateLink} onChange={(event) => setRotateLink(event.target.checked)} />
            <span>
              <strong>보안상 새 링크 발급</strong>
              <small>기존 링크가 외부에 노출됐다고 판단될 때만 선택하세요. 선택하지 않으면 같은 파트너 링크를 재사용합니다.</small>
            </span>
          </label>

          <div className="admin-revision-actions">
            <button className="button secondary" type="button" onClick={onClose} disabled={submitting}>취소</button>
            <button className="button primary" type="submit" disabled={submitting || !trimmedReason}>{submitting ? "요청 중" : rotateLink ? "새 링크로 수정 요청" : "기존 링크로 수정 요청"}</button>
          </div>
        </form>
      </section>
    </div>
  );
}

function googleIntegrationLabel(kinds: AdminSubmissionSummary["googleIntegrations"]): string {
  return `구글 ${kinds.map((kind) => kind === "review" ? "리뷰" : "예약").join("·")} 연동`;
}

function SpotPanel({ submission, onSave }: { submission: AdminSubmissionDetail; onSave: (spotId: number | null) => Promise<void> }) {
  const [value, setValue] = useState(submission.spotId ? String(submission.spotId) : "");
  const [submitting, setSubmitting] = useState(false);
  const spotId = submission.spotId;
  const nextSpotId = Number(value);
  const canSave = nextSpotId > 0 && nextSpotId !== spotId;

  async function save(target: number | null) {
    setSubmitting(true);
    try {
      await onSave(target);
      if (target === null) setValue("");
    } catch {
      // The parent displays the API error toast.
    } finally {
      setSubmitting(false);
    }
  }

  function submitSpot(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (canSave) void save(nextSpotId);
  }

  return (
    <section className="admin-info-section admin-partner-account-section">
      <div>
        <h3>크리에이트립 스팟</h3>
        <p className="admin-helper-text">
          {submission.googleIntegrations.length > 0 ? `${googleIntegrationLabel(submission.googleIntegrations)}을 요청한 업체입니다. ` : ""}
          스팟을 만든 뒤 번호를 입력하면 제출 목록의 구글 연동 표시가 사라집니다.
        </p>
      </div>
      {spotId ? (
        <div className="admin-alert success" role="status">
          스팟 {spotId} · <a href={`https://creatrip.com/en/spot/${spotId}`} target="_blank" rel="noreferrer">크리에이트립에서 열기</a>
        </div>
      ) : null}
      <form className="admin-spot-form" onSubmit={submitSpot}>
        <input value={value} onChange={(event) => setValue(event.target.value.replace(/\D/g, ""))} inputMode="numeric" placeholder="스팟 번호 (예: 15621)" aria-label="스팟 번호" disabled={submitting} />
        <button className="button primary" type="submit" disabled={submitting || !canSave}>{submitting ? "저장 중" : spotId ? "번호 변경" : "스팟 연결"}</button>
        {spotId ? <button className="button secondary" type="button" onClick={() => { if (window.confirm("스팟 번호를 지우면 구글 연동 표시가 다시 나타납니다. 지울까요?")) void save(null); }} disabled={submitting}>지우기</button> : null}
      </form>
    </section>
  );
}

function PartnershipPanel({ submission, onCreate }: { submission: AdminSubmissionDetail; onCreate: () => Promise<void> }) {
  const [submitting, setSubmitting] = useState(false);
  const isCreating = submission.partnershipCreationStatus === "CREATING";
  const needsReview = submission.partnershipCreationStatus === "REVIEW_REQUIRED";
  const code = submission.partnershipCode;
  const adminOrigin = window.location.hostname === "meet-the-world.creatrip.team" ? "https://admin.creatrip.com" : "https://develop-admin.creatrip.com";

  async function submitPartnership() {
    if (code || isCreating || needsReview) return;
    if (!window.confirm("사업자등록증을 OCR로 분석해 Product 거래처를 생성할까요?")) return;
    setSubmitting(true);
    try {
      await onCreate();
    } catch {
      // The parent displays the API error toast.
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="admin-info-section admin-partner-account-section">
      <div>
        <h3>Product 거래처 생성</h3>
        <p className="admin-helper-text">제출된 사업자등록증을 분석하고 신청 카테고리를 매핑해 Product partnership을 생성합니다.</p>
      </div>
      {code ? (
        <div className="admin-alert success" role="status">
          Partnership code {code} · <a href={`${adminOrigin}/partnership/${code}`} target="_blank" rel="noreferrer">Product Admin에서 열기</a>
        </div>
      ) : (
        <div className="admin-partner-account-action">
          <span>{needsReview ? "생성 결과가 불확실합니다. 중복 생성을 막기 위해 Product Admin에서 사업자번호를 확인해주세요." : isCreating ? "다른 요청에서 생성 중입니다. 잠시 후 제출물을 다시 열어 확인해주세요." : submission.partnershipCreationStatus === "FAILED" ? "사업자등록증 분석이 실패했습니다. 제출 정보를 확인한 뒤 다시 시도할 수 있습니다." : "사업자등록증 OCR 결과를 확인한 뒤 거래처가 생성됩니다."}</span>
          <button className="button primary" type="button" onClick={submitPartnership} disabled={submitting || isCreating || needsReview}>
            {submitting ? "생성 중" : needsReview ? "확인 필요" : isCreating ? "처리 중" : "Product 거래처 생성"}
          </button>
        </div>
      )}
    </section>
  );
}

function PartnerAccountPanel({ submission, onCreate }: { submission: AdminSubmissionDetail; onCreate: (input: PartnerAccountInput) => Promise<void> }) {
  const company = readRecord(submission.data, "company");
  const reservation = readRecord(submission.data, "reservation");
  const hasStoredPassword = readString(reservation, "adminPassword") === "********";
  const accountId = submission.adminLoginId ?? "";
  const accountEmail = submission.contactEmail ?? readString(company, "contactEmail");
  const accountName = readString(company, "contactName");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submitAccount(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!window.confirm(`${accountId} ID로 Creatrip 파트너 계정을 생성할까요?`)) return;
    setSubmitting(true);
    try {
      await onCreate({ password: password || undefined });
      setPassword("");
    } catch {
      // The parent displays the API error toast.
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="admin-info-section admin-partner-account-section">
      <div>
        <h3>파트너 Admin 계정 생성</h3>
        <p className="admin-helper-text">저장된 비밀번호를 그대로 사용하면 브라우저에서 다시 보내지 않고 Worker가 Creatrip GraphQL에 직접 전달합니다.</p>
      </div>
      <form className="admin-partner-account-form" onSubmit={submitAccount}>
        <label><span>ID (제출값)</span><input value={accountId} readOnly /></label>
        <label><span>이름 (제출값)</span><input value={accountName} readOnly /></label>
        <label><span>이메일 (제출값)</span><input value={accountEmail} type="email" readOnly /></label>
        <label>
          <span>새 비밀번호 {hasStoredPassword ? "(선택)" : "(필수)"}</span>
          <input value={password} onChange={(event) => setPassword(event.target.value)} type="password" autoComplete="new-password" minLength={4} required={!hasStoredPassword} placeholder={hasStoredPassword ? "비워두면 제출된 비밀번호 사용" : "4자 이상 입력"} />
        </label>
        <div className="admin-partner-account-action">
          <span>{hasStoredPassword ? "제출된 비밀번호가 있으며 화면에는 표시되지 않습니다." : "제출된 비밀번호가 없어 새 비밀번호가 필요합니다."}</span>
          <button className="button primary" type="submit" disabled={submitting}>{submitting ? "생성 중" : "파트너 계정 생성"}</button>
        </div>
      </form>
    </section>
  );
}

export function AdminPasswordInfoRow({ submissionId, hasStoredPassword }: { submissionId: string; hasStoredPassword: boolean }) {
  const [password, setPassword] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setPassword(null);
    setError(null);
  }, [submissionId]);

  async function revealPassword() {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      setPassword(await revealAdminPassword(submissionId));
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setLoading(false);
    }
  }

  const isShowing = password !== null;
  return (
    <div className="admin-kv-row">
      <span>임시 비밀번호</span>
      <div className="admin-secret-value" aria-live="polite">
        {isShowing ? <code>{password}</code> : <strong>{hasStoredPassword ? "입력됨" : "-"}</strong>}
        {hasStoredPassword && (
          <button className="button secondary compact" type="button" onClick={isShowing ? () => setPassword(null) : revealPassword} aria-disabled={loading || undefined} aria-label={isShowing ? "임시 비밀번호 숨기기" : "임시 비밀번호 확인"}>
            {loading ? "확인 중" : isShowing ? "숨기기" : "확인"}
          </button>
        )}
        {error && <small className="admin-secret-error" role="alert">{error}</small>}
      </div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return <div className="admin-kv-row"><span>{label}</span><strong>{value || "-"}</strong></div>;
}

export type AdminFileGroup = {
  key: string;
  label: string;
  description: string;
  rank: number;
  files: AdminSubmissionFile[];
};

const ADMIN_FILE_CATEGORY_META: Record<string, { label: string; description: string; rank: number }> = {
  hero_image: { label: "대표사진", description: "상품 썸네일과 상단 대표 이미지로 쓰는 사진입니다.", rank: 10 },
  listing_photo: { label: "상품 페이지 등재 사진", description: "상세 페이지 갤러리에 들어가는 서브 사진입니다.", rank: 20 },
  interior_exterior: { label: "공간 / 현장 사진", description: "입구, 내부, 대기공간, 이용 공간처럼 방문 안내에 쓰는 사진입니다.", rank: 30 },
  treatment_photo: { label: "진행 장면 / 장비 사진", description: "시술, 체험, 메뉴, 차량, 소품 등 상품 이해를 돕는 사진입니다.", rank: 40 },
  before_after_photo: { label: "전후 / 결과 예시 사진", description: "전후 비교, 포트폴리오, 결과물 예시 자료입니다.", rank: 50 },
  model_photo: { label: "모델 / 홍보 이미지", description: "브랜드 화보, 모델컷, 홍보 이미지입니다.", rank: 60 },
  logo: { label: "로고", description: "브랜드 로고 이미지입니다.", rank: 70 },
  marketing_asset: { label: "브로슈어 / 참고 자료", description: "소개서, 메뉴판, 브로슈어 등 콘텐츠 제작 참고 자료입니다.", rank: 80 },
  business_registration: { label: "사업자등록증", description: "계약 및 거래처 등록용 서류입니다.", rank: 120 },
  medical_tourism_certificate: { label: "외국인유치의료기관 등록증", description: "의료기관 입점 관련 증빙 서류입니다.", rank: 130 },
  bankbook_copy: { label: "정산 통장 사본", description: "정산 계좌 확인용 서류입니다.", rank: 140 },
  other_document: { label: "기타 서류", description: "그 외 추가로 제출한 서류입니다.", rank: 150 },
};

function AdminSubmissionFiles({ submission }: { submission: AdminSubmissionDetail }) {
  const groups = useMemo(() => buildAdminFileGroups(submission), [submission]);
  return (
    <section className="admin-info-section">
      <h3>첨부 파일</h3>
      {submission.files.length === 0 ? <p className="admin-helper-text">첨부 파일이 없습니다.</p> : (
        <div className="admin-file-group-list">
          {groups.map((group) => (
            <section className="admin-file-group" key={group.key}>
              <div className="admin-file-group-head">
                <div>
                  <strong>{group.label}</strong>
                  <p>{group.description}</p>
                </div>
                <span>{group.files.length.toLocaleString()}개</span>
              </div>
              <div className="admin-file-card-grid">
                {group.files.map((file) => <AdminFilePreviewCard file={file} key={file.id} />)}
              </div>
            </section>
          ))}
        </div>
      )}
    </section>
  );
}

function AdminFilePreviewCard({ file }: { file: AdminSubmissionFile }) {
  const isImage = file.contentType.startsWith("image/");
  const isVideo = file.contentType.startsWith("video/");
  const isPdf = file.contentType === "application/pdf";
  const mediaLabel = isImage ? "이미지" : isVideo ? "영상" : isPdf ? "PDF" : "파일";
  const previewPath = adminFilePreviewPath(file);
  return (
    <a className="admin-file-card" href={file.adminDownloadPath} target="_blank" rel="noreferrer" title={file.fileName}>
      <div className={`admin-file-thumb ${isImage ? "image" : isVideo ? "video" : "file"}`}>
        {isImage ? (
          <img src={previewPath} alt={`${file.fileName} 미리보기`} loading="lazy" />
        ) : isVideo ? (
          <>
            <video src={previewPath} muted playsInline preload="metadata" aria-label={`${file.fileName} 영상 미리보기`} />
            <span className="admin-file-play-badge" aria-hidden="true">▶</span>
          </>
        ) : (
          <span>{isPdf ? "PDF" : "FILE"}</span>
        )}
      </div>
      <div className="admin-file-card-meta">
        <strong>{file.fileName}</strong>
        <span>{mediaLabel} · {formatBytes(file.size)}</span>
        <small>{formatDate(file.uploadedAt)} · 열기/다운로드</small>
      </div>
    </a>
  );
}

export function adminFilePreviewPath(file: AdminSubmissionFile): string {
  if (file.publicUrl || file.adminDownloadPath.includes("/original/spot/meet-the-world/")) return file.adminDownloadPath;
  return `${file.adminDownloadPath}${file.adminDownloadPath.includes("?") ? "&" : "?"}preview=1`;
}

export function buildAdminFileGroups(submission: AdminSubmissionDetail): AdminFileGroup[] {
  const groupMap = new Map<string, AdminFileGroup>();
  for (const file of submission.files) {
    const meta = getAdminFileGroupMeta(file, submission);
    const group = groupMap.get(meta.key) ?? { ...meta, files: [] };
    group.files.push(file);
    groupMap.set(meta.key, group);
  }
  return Array.from(groupMap.values()).sort((left, right) => left.rank - right.rank || left.label.localeCompare(right.label));
}

function getAdminFileGroupMeta(file: AdminSubmissionFile, submission: AdminSubmissionDetail): Omit<AdminFileGroup, "files"> {
  if (file.category === "doctor_staff_photo") {
    const index = fieldIndexFromId(file.fieldId, "doctorPhoto-");
    const doctorName = index == null ? "" : readString(readArray(submission.data, "doctors")[index] as Record<string, unknown> | undefined ?? {}, "name");
    const label = doctorName ? `${doctorName} 사진` : `의료진 / 전문가 ${index == null ? "" : index + 1} 사진`.trim();
    return { key: `doctor_staff_photo:${file.fieldId ?? "legacy"}`, label, description: "의료진/전문가 소개에 사용할 대표 사진입니다.", rank: 90 + (index ?? 0) };
  }
  if (file.category === "product_detail_photo") {
    const index = fieldIndexFromId(file.fieldId, "productDetailPhoto-");
    const productName = index == null ? "" : readString(readArray(submission.data, "products")[index] as Record<string, unknown> | undefined ?? {}, "optionName");
    const label = productName ? `${productName} 관련 사진` : `상품별 상세 사진 ${index == null ? "" : index + 1}`.trim();
    return { key: `product_detail_photo:${file.fieldId ?? "default"}`, label, description: "상품 옵션 설명에 함께 보여줄 보조 사진입니다.", rank: 100 + (index ?? 0) };
  }
  const fallback = ADMIN_FILE_CATEGORY_META[file.category] ?? { label: file.category || "기타 첨부", description: "추가로 제출된 첨부 파일입니다.", rank: 999 };
  return { key: file.category || "unknown", ...fallback };
}

function fieldIndexFromId(fieldId: string | undefined, prefix: string): number | null {
  if (!fieldId?.startsWith(prefix)) return null;
  const index = Number.parseInt(fieldId.slice(prefix.length), 10);
  return Number.isFinite(index) ? index : null;
}

export type AdminDetailRow = { id: string; label: string; value: string; kind?: "adminPassword" };
type AdminDetailSection = { title: string; rows: AdminDetailRow[] };
type ReviewField = { key: string; label: string; suffix?: string };
type ReviewManifest = NonNullable<AdminSubmissionDetail["presentationManifest"]>;

const REVIEW_VALUE_LABELS: Record<string, string> = {
  approval_required: "확인 후 확정",
  auto_confirm: "자동 확정",
  email: "이메일",
  kakao: "카카오톡 단톡방",
  creatrip_admin: "크리에이트립 Admin",
  none: "소통 채널 불필요",
  free_reservation_upsell: "무료 예약 + 업셀링",
  deposit: "보증금 결제",
  deposit_upsell: "보증금 결제 + 업셀링",
  not_charged: "무료",
  charged: "유료",
  yes: "연동함",
  no: "연동하지 않음",
};

export function buildDetailSections(submission: AdminSubmissionDetail): Array<{ title: string; rows: AdminDetailRow[] }> {
  const data = submission.data;
  const company = readRecord(data, "company");
  const reservation = readRecord(data, "reservation");
  const contract = readRecord(data, "contract");
  const content = readRecord(data, "content");
  const language = readRecord(data, "language");
  const policy = readRecord(data, "policy");
  const documents = readRecord(data, "documents");
  const products = readArray(data, "products");
  const doctors = readArray(data, "doctors");
  const procedures = readArray(data, "procedures");

  const baseSections: AdminDetailSection[] = [
    {
      title: "업체 기본 정보",
      rows: [
        { id: "category", label: "카테고리", value: submission.category.label || submission.category.categoryId },
        ...reviewRows("company", company, [
          { key: "storeNameKo", label: "상점명 / 병원명(한국어)" }, { key: "storeNameEn", label: "상점명 / 병원명(영어)" },
          { key: "storeNameJa", label: "상점명 / 병원명(일본어)" }, { key: "storeNameZh", label: "상점명 / 병원명(중국어)" },
          { key: "address", label: "주소" }, { key: "representativePhone", label: "업체 대표 전화번호" },
          { key: "kakaoMapsUrl", label: "카카오맵 링크" }, { key: "googleMapsUrl", label: "구글맵 링크" },
          { key: "websiteUrl", label: "공식 홈페이지" }, { key: "instagramUrl", label: "인스타그램 / SNS" },
          { key: "contentReferenceUrl", label: "콘텐츠 참고 URL" }, { key: "contactName", label: "업체 담당자명" },
          { key: "contactRole", label: "담당자 직함" }, { key: "contactDepartment", label: "담당자 부서" },
          { key: "contactEmail", label: "담당자 이메일" }, { key: "contactPhone", label: "담당자 연락처" },
          { key: "taxInvoiceContactName", label: "세금계산서 담당자명" }, { key: "taxInvoiceContactPhone", label: "세금계산서 담당자 연락처" },
          { key: "taxInvoiceContactEmail", label: "세금계산서 담당자 이메일" },
        ]),
      ],
    },
    {
      title: "예약 운영과 계정",
      rows: [
        { id: "reservation.useContactEmailForNotifications", label: "예약 알림 이메일 수신", value: reviewBooleanChoice(reservation, "useContactEmailForNotifications", "담당자 이메일로 수신", "별도 이메일로 수신") },
        { id: "reservation.useContactPhoneForAlimtalk", label: "알림톡 수신", value: reviewBooleanChoice(reservation, "useContactPhoneForAlimtalk", "담당자 연락처로 수신", "별도 번호로 수신") },
        ...reviewRows("reservation", reservation, [
          { key: "notificationEmails", label: "예약 알림 이메일" }, { key: "notificationPhones", label: "알림톡 받을 번호" },
          { key: "bookableStartOffsetDays", label: "예약 시작", suffix: "일 후부터" }, { key: "bookableWindowMonths", label: "예약 오픈 기간", suffix: "개월" },
          { key: "businessHours", label: "업체 영업시간" }, { key: "reservationHours", label: "크리에이트립 예약 가능 시간" },
          { key: "reservationUnit", label: "예약 간격" }, { key: "confirmationMode", label: "예약 확정 방식" },
          { key: "communicationChannel", label: "예약 확인 채널" }, { key: "adminLoginId", label: "희망 Admin ID" },
        ]),
        { id: "reservation.adminAccountNeeded", label: "크리에이트립 Admin 계정 신청", value: readBoolean(reservation, "adminAccountNeeded") ? "신청함" : "" },
        { id: "reservation.adminPassword", label: "임시 비밀번호", value: readString(reservation, "adminPassword"), kind: "adminPassword" },
      ],
    },
    {
      title: "계약 조건",
      rows: reviewRows("contract", contract, [
        { key: "contractType", label: "계약 구조" }, { key: "upsellCommissionRate", label: "업셀링 수수료율", suffix: "%" },
        { key: "offeredContractTypes", label: "제안 계약 구조" }, { key: "offeredUpsellCommissionRate", label: "제안 업셀링 수수료율", suffix: "%" },
        { key: "depositPolicyMemo", label: "보증금 관련 요청사항" }, { key: "settlementMemo", label: "정산 관련 특이사항" },
      ]),
    },
    {
      title: `상품 옵션 (${products.length.toLocaleString()}개)`,
      rows: repeaterRows("products", "상품", products, [
        { key: "optionName", label: "옵션명" }, { key: "listPrice", label: "정가" }, { key: "salePrice", label: "판매가" },
        { key: "depositOrSettlementPrice", label: "보증금 / 정산가" }, { key: "freeReservationLabel", label: "무료예약 표기" },
        { key: "depositAmount", label: "보증금" }, { key: "settlementPrice", label: "정산가" }, { key: "maxQuantityPerSlot", label: "한 타임 최대 수량" },
        { key: "duration", label: "소요시간" }, { key: "includedItems", label: "포함 항목" }, { key: "excludedItems", label: "제외 항목" },
        { key: "recommendationReason", label: "추천 이유" }, { key: "detailDescription", label: "상품 상세 설명" },
        { key: "effectDuration", label: "효과 지속 기간" }, { key: "recommendedTargets", label: "추천 대상" }, { key: "productPrecautions", label: "상품별 주의사항" },
      ]),
    },
    {
      title: "콘텐츠 정보",
      rows: reviewRows("content", content, [
        { key: "catchphrase", label: "짧은 소개 문구" }, { key: "sellingPointTags", label: "업체 장점" },
        { key: "touristConvenienceOptions", label: "관광객 이용 편의" }, { key: "primarySellingPoint", label: "가장 강하게 어필할 장점" },
        { key: "targetCustomers", label: "추천 고객" }, { key: "targetCustomerConcernDetail", label: "특정 고민 상세" },
        { key: "heroImageMemo", label: "대표사진 안내" }, { key: "recommendationReason", label: "외국인 고객 추천 이유" },
        { key: "creatripExclusiveBenefitStatus", label: "크리에이트립 단독 혜택 여부" }, { key: "creatripExclusiveBenefit", label: "크리에이트립 단독 혜택 내용" },
        { key: "reviewBenefit", label: "리뷰작성 혜택" }, { key: "clinicStrengths", label: "병원 특장점" },
        { key: "serviceEnvironment", label: "서비스 / 이용환경" }, { key: "referenceLinks", label: "참고 링크" },
        { key: "googleReviewIntegration", label: "구글 리뷰 연동" }, { key: "googleBookingIntegration", label: "구글 예약 연동" },
      ]),
    },
    {
      title: "외국어 응대와 서면 주의사항",
      rows: reviewRows("language", language, [
        { key: "availableLanguages", label: "가능 외국어 및 응대 방식" }, { key: "serviceScope", label: "외국어 응대 범위" },
        { key: "cautionLanguages", label: "서면 주의사항 제공 언어" }, { key: "interpreterAvailability", label: "통역 가능 여부" },
      ]),
    },
    {
      title: "의료진과 시술",
      rows: [
        ...repeaterRows("doctors", "의료진", doctors, [
          { key: "name", label: "이름" }, { key: "role", label: "직함" }, { key: "profileText", label: "이력 / 소개" }, { key: "referenceUrl", label: "이력 참고 링크" },
        ]),
        ...repeaterRows("procedures", "시술", procedures, [
          { key: "treatmentName", label: "시술명" }, { key: "description", label: "설명" }, { key: "duration", label: "소요시간" }, { key: "effectDuration", label: "효과 지속 기간" },
        ]),
      ],
    },
    {
      title: "이용 정책과 주의사항",
      rows: reviewRows("policy", policy, [
        { key: "cancellationRefundPolicy", label: "취소 및 환불규정" }, { key: "visitFlow", label: "이용 순서" },
        { key: "bookingQuestionChecklist", label: "예약 시 받을 정보" }, { key: "bookingQuestions", label: "추가 예약 질문" },
        { key: "consultationFeePolicy", label: "상담비" }, { key: "consultationFeeAmount", label: "상담비 금액" },
        { key: "minorPolicy", label: "미성년자 정책" }, { key: "passportNeeded", label: "여권 필요 여부" },
        { key: "healthRestrictions", label: "건강 관련 제한사항" }, { key: "otherPrecautions", label: "기타 주의사항" },
      ]),
    },
    {
      title: "제출 확인",
      rows: [
        { id: "documents.imageUsageConsent", label: "이미지 사용 동의", value: readBoolean(documents, "imageUsageConsent") ? "동의함" : "" },
        { id: "documents.documentAccuracyConsent", label: "정보·서류 정확성 확인", value: readBoolean(documents, "documentAccuracyConsent") ? "확인함" : "" },
      ],
    },
  ];
  const manifestLabels = createManifestLabels(submission.presentationManifest);
  const labeledSections = applyManifestLabels(baseSections, manifestLabels);
  const displayedRowIds = new Set(labeledSections.flatMap((section) => section.rows.map((row) => row.id)));
  return [...labeledSections, ...buildAdditionalReviewSections(data, submission.presentationManifest, displayedRowIds)];
}

function createManifestLabels(manifest: ReviewManifest | undefined): Map<string, string> {
  const labels = new Map<string, string>();
  for (const section of manifest?.sections ?? []) {
    for (const field of section.fields) labels.set(field.path, field.label);
    for (const repeater of section.repeaters) {
      for (const field of repeater.fields) labels.set(`${repeater.path}.${field.path}`, field.label);
    }
  }
  return labels;
}

function applyManifestLabels(sections: AdminDetailSection[], labels: ReadonlyMap<string, string>): AdminDetailSection[] {
  return sections.map((section) => ({
    ...section,
    rows: section.rows.map((row) => ({ ...row, label: manifestLabelForRow(row, labels) })),
  }));
}

function manifestLabelForRow(row: AdminDetailRow, labels: ReadonlyMap<string, string>): string {
  const directLabel = labels.get(row.id);
  if (directLabel) return directLabel;
  const repeaterMatch = row.id.match(/^(.*)\.(\d+)\.([^.]+)$/);
  if (!repeaterMatch) return row.label;
  const [, collectionPath, , fieldPath] = repeaterMatch;
  const fieldLabel = labels.get(`${collectionPath}.${fieldPath}`);
  if (!fieldLabel) return row.label;
  const separatorIndex = row.label.lastIndexOf(" · ");
  return separatorIndex >= 0 ? `${row.label.slice(0, separatorIndex)} · ${fieldLabel}` : fieldLabel;
}

function buildAdditionalReviewSections(data: Record<string, unknown>, manifest: ReviewManifest | undefined, displayedRowIds: ReadonlySet<string>): AdminDetailSection[] {
  const renderedIds = new Set(displayedRowIds);
  const sections = (manifest?.sections ?? []).flatMap((section) => {
    const rows = [
      ...section.fields.flatMap((field) => manifestFieldRow(data, field.path, field.label, field.options, renderedIds)),
      ...section.repeaters.flatMap((repeater) => manifestRepeaterRows(data, repeater.path, repeater.label, repeater.fields, renderedIds)),
    ];
    return rows.length ? [{ title: section.title, rows }] : [];
  });
  const fallbackRows = flattenReviewData(data).flatMap(({ path, value }) => {
    if (renderedIds.has(path) || !isReviewableDataPath(path)) return [];
    const displayValue = formatAdditionalReviewValue(value);
    if (!displayValue) return [];
    renderedIds.add(path);
    return [{ id: path, label: fallbackReviewLabel(path), value: displayValue }];
  });
  return fallbackRows.length ? [...sections, { title: "기타 제출 항목", rows: fallbackRows }] : sections;
}

function manifestFieldRow(data: Record<string, unknown>, path: string, label: string, options: ReviewManifest["sections"][number]["fields"][number]["options"] | undefined, renderedIds: Set<string>): AdminDetailRow[] {
  if (renderedIds.has(path) || !isReviewableDataPath(path)) return [];
  const value = formatAdditionalReviewValue(readReviewPath(data, path), options);
  renderedIds.add(path);
  return [{ id: path, label, value }];
}

function manifestRepeaterRows(data: Record<string, unknown>, path: string, label: string, fields: ReviewManifest["sections"][number]["repeaters"][number]["fields"], renderedIds: Set<string>): AdminDetailRow[] {
  const entries = readReviewPath(data, path);
  if (!Array.isArray(entries)) return [];
  return entries.flatMap((entry, index) => {
    if (!isRecord(entry)) return [];
    return fields.flatMap((field) => {
      const id = `${path}.${index}.${field.path}`;
      if (renderedIds.has(id) || !isReviewableDataPath(id)) return [];
      const value = formatAdditionalReviewValue(entry[field.path], field.options);
      renderedIds.add(id);
      return [{ id, label: `${label} ${index + 1} · ${field.label}`, value }];
    });
  });
}

function readReviewPath(source: Record<string, unknown>, path: string): unknown {
  let current: unknown = source;
  for (const segment of path.split(".")) {
    if (!isRecord(current)) return undefined;
    current = current[segment];
  }
  return current;
}

function flattenReviewData(value: unknown, path = ""): Array<{ path: string; value: unknown }> {
  if (Array.isArray(value)) {
    return value.flatMap((item, index) => flattenReviewData(item, path ? `${path}.${index}` : String(index)));
  }
  if (!isRecord(value)) return path ? [{ path, value }] : [];
  return Object.entries(value).flatMap(([key, item]) => flattenReviewData(item, path ? `${path}.${key}` : key));
}

function isReviewableDataPath(path: string): boolean {
  const [root] = path.split(".");
  if (!root || root === "category" || root === "formConfig" || root.startsWith("_")) return false;
  return !/(password|passwd|secret|credential|token|api[_-]?key|access[_-]?key|private[_-]?key|authorization|cookie|storage[_-]?(key|url)|deletepath|downloadpath)/i.test(path);
}

function formatAdditionalReviewValue(value: unknown, options?: ReviewManifest["sections"][number]["fields"][number]["options"]): string {
  if (typeof value === "boolean") return value ? "예" : "아니오";
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (typeof value !== "string") return "";
  const text = value.trim();
  if (!text || !options?.length) return text;
  const labelsByValue = new Map(options.map((option) => [option.value, option.label]));
  const values = text.split(/\n|,/).map((item) => item.trim()).filter(Boolean);
  if (!values.length || !values.every((item) => labelsByValue.has(item))) return text;
  return values.map((item) => labelsByValue.get(item) ?? item).join("\n");
}

function fallbackReviewLabel(path: string): string {
  return path.split(".").map((segment) => {
    if (/^\d+$/.test(segment)) return `${Number(segment) + 1}번`;
    return segment.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/[_-]/g, " ");
  }).join(" · ");
}

function reviewRows(prefix: string, source: Record<string, unknown>, fields: ReviewField[]): AdminDetailRow[] {
  return fields.map((field) => ({
    id: `${prefix}.${field.key}`,
    label: field.label,
    value: formatReviewValue(readString(source, field.key), field.suffix),
  }));
}

function repeaterRows(prefix: string, label: string, entries: unknown[], fields: ReviewField[]): AdminDetailRow[] {
  return entries.flatMap((entry, index) => {
    const source = isRecord(entry) ? entry : {};
    return fields.map((field) => ({
      id: `${prefix}.${index}.${field.key}`,
      label: `${label} ${index + 1} · ${field.label}`,
      value: formatReviewValue(readString(source, field.key), field.suffix),
    }));
  });
}

function formatReviewValue(value: string, suffix?: string): string {
  if (!value) return "";
  return `${REVIEW_VALUE_LABELS[value] ?? value}${suffix ?? ""}`;
}

function reviewBooleanChoice(source: Record<string, unknown>, key: string, trueLabel: string, falseLabel: string): string {
  const value = source[key];
  if (typeof value !== "boolean") return "";
  return value ? trueLabel : falseLabel;
}

function readRecord(source: Record<string, unknown>, key: string): Record<string, unknown> {
  const value = source[key];
  return typeof value === "object" && value !== null && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function readArray(source: Record<string, unknown>, key: string): unknown[] {
  const value = source[key];
  return Array.isArray(value) ? value : [];
}

function readString(source: Record<string, unknown>, key: string): string {
  const value = source[key];
  return typeof value === "string" ? value.trim() : "";
}

function readBoolean(source: Record<string, unknown>, key: string): boolean {
  return source[key] === true;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function inviteCategoryLabel(categoryId: string | null): string {
  if (!categoryId) return "미분류";
  const option = CATEGORY_OPTIONS.find((candidate) => candidate.value === categoryId);
  return option?.label ?? getCategoryProfile(categoryId)?.label ?? categoryId;
}

function isInviteExpired(expiresAt: string): boolean {
  const timestamp = Date.parse(expiresAt);
  return Number.isFinite(timestamp) && timestamp < Date.now();
}

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("ko-KR", { timeZone: "Asia/Seoul", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).format(date);
}

function formatBytes(value: number): string {
  if (!value) return "0 B";
  if (value < 1024 * 1024) return `${Math.round(value / 1024).toLocaleString()} KB`;
  return `${(value / 1024 / 1024).toFixed(1)} MB`;
}

function copyTextWithFallback(text: string) {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "true");
  textarea.style.position = "absolute";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
}

function errorMessage(error: unknown): string {
  if (error instanceof AdminApiError) {
    if (error.status === 401) return "로그인이 필요합니다.";
    if (error.status === 403) return "관리자 권한이 필요합니다.";
    return error.message;
  }
  return error instanceof Error ? error.message : "요청을 처리하지 못했습니다.";
}
